import { useMutation, useQuery } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import {
  SyntheticEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Control,
  Controller,
  FieldArrayWithId,
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  useFieldArray,
  useForm,
  Resolver,
  useWatch,
} from "react-hook-form";
import {
  MapContainer,
  TileLayer,
  Polygon,
  Polyline,
  Marker,
  Popup,
  Tooltip,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L, { LatLngBoundsExpression, LatLngExpression } from "leaflet";
import "leaflet/dist/leaflet.css";
import iconRetinaUrl from "leaflet/dist/images/marker-icon-2x.png";
import iconUrl from "leaflet/dist/images/marker-icon.png";
import shadowUrl from "leaflet/dist/images/marker-shadow.png";

import * as yup from "yup";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import { PageContainer } from "../../components/PageContainer";
import { LoadingOverlay } from "../../components/Loading";
import { MapTabs } from "./MapTabs";
import { GET_CONFIG, UPDATE_CONFIG } from "../config/graphql";
import { GET_FESTIVAL_SETTINGS } from "../settings/graphql";
import { getRoleDeeplink } from "./encryptRole";
import { Config } from "../../__generated__/graphql";
import { UpdateMiscConfigButton } from "../links/updateMiscConfig";
import { VendorPicker } from "../../components/VendorPicker";
import { StagePicker } from "../../components/StagePicker";
import { useCurrentUserEmail, useIsSuperAdmin } from "../../App";

L.Icon.Default.mergeOptions({
  iconRetinaUrl,
  iconUrl,
  shadowUrl,
});

type CoordinateFormValue = {
  lat?: number;
  lng?: number;
};

type MapAreaFormValue = {
  id: string;
  name: string;
  type: string;
  description?: string | null;
  color: string;
  coordinates: CoordinateFormValue[];
  tags?: string[];
  hidden?: boolean;
};

type MapPointFormValue = {
  id: string;
  name: string;
  category: string;
  description?: string | null;
  lat?: number;
  lng?: number;
  vendorId?: string | null;
  stageId?: string | null;
  iconName?: string | null;
  iconScale?: number | null;
  tags?: string[];
  hidden?: boolean;
};

type MapLineFormValue = {
  id: string;
  name: string;
  color: string;
  coordinates: CoordinateFormValue[];
  tags?: string[];
  hidden?: boolean;
};

type InteractiveMapFormValues = {
  areas: MapAreaFormValue[];
  pointsOfInterest: MapPointFormValue[];
  lines: MapLineFormValue[];
};

type MapFocusTarget = {
  lat: number;
  lng: number;
};

const areaTypes = [
  { value: "camping", label: "Camping Field" },
  { value: "arena", label: "Arena" },
  { value: "parking", label: "Parking" },
  { value: "other", label: "Other" },
];

const pointCategories = [
  { value: "stage", label: "Stage" },
  { value: "food", label: "Food Vendor" },
  { value: "vendor", label: "Vendor" },
  { value: "bar", label: "Bar" },
  { value: "merch", label: "Merch" },
  { value: "info", label: "Info Point" },
  { value: "accessibility", label: "Accessibility" },
  { value: "other", label: "Other" },
  { value: "vip", label: "VIP" },
  { value: "parking", label: "Parking" },
  { value: "camping", label: "Camping" },
  { value: "glamping", label: "Glamping" },
];

const vendorCategories = ["food", "vendor"];

const coordinateSchema = yup.object({
  lat: yup
    .number()
    .typeError("Enter a latitude between -90 and 90")
    .min(-90, "Latitude must be >= -90")
    .max(90, "Latitude must be <= 90")
    .required("Latitude is required"),
  lng: yup
    .number()
    .typeError("Enter a longitude between -180 and 180")
    .min(-180, "Longitude must be >= -180")
    .max(180, "Longitude must be <= 180")
    .required("Longitude is required"),
});

const areaSchema = yup.object({
  id: yup.string().required(),
  name: yup.string().required("Area name is required"),
  type: yup.string().required("Select an area type"),
  description: yup.string().nullable(),
  color: yup
    .string()
    .matches(/^#([0-9a-f]{3}){1,2}$/i, "Enter a valid hex color")
    .required(),
  coordinates: yup
    .array()
    .of(coordinateSchema)
    .min(3, "Areas need at least 3 points to draw a shape"),
  tags: yup.array().of(yup.string().required()).optional(),
  hidden: yup.boolean().optional(),
});

const pointSchema = yup.object({
  id: yup.string().required(),
  name: yup.string().required("Name is required"),
  category: yup.string().required("Select a category"),
  description: yup.string().nullable(),
  lat: coordinateSchema.fields.lat,
  lng: coordinateSchema.fields.lng,
  vendorId: yup.string().nullable(),
  stageId: yup
    .string()
    .nullable()
    .when("category", {
      is: "stage",
      then: (schema) => schema.required("Select a stage for this point"),
    }),
  iconName: yup.string().nullable().optional(),
  iconScale: yup.number().nullable().optional(),
  tags: yup.array().of(yup.string().required()).optional(),
  hidden: yup.boolean().optional(),
});

const lineSchema = yup.object({
  id: yup.string().required(),
  name: yup.string().required("Line name is required"),
  color: yup
    .string()
    .matches(/^#([0-9a-f]{3}){1,2}$/i, "Enter a valid hex color")
    .required(),
  coordinates: yup
    .array()
    .of(coordinateSchema)
    .min(2, "Lines need at least 2 points"),
  tags: yup.array().of(yup.string().required()).optional(),
  hidden: yup.boolean().optional(),
});

const formSchema = yup.object({
  areas: yup.array().of(areaSchema).default([]),
  pointsOfInterest: yup.array().of(pointSchema).default([]),
  lines: yup.array().of(lineSchema).default([]),
});

const emptyForm: InteractiveMapFormValues = {
  areas: [],
  pointsOfInterest: [],
  lines: [],
};

const createId = (prefix: string) =>
  `${prefix}-${Math.random().toString(36).slice(2, 9)}`;

const createEmptyLine = (): MapLineFormValue => ({
  id: createId("line"),
  name: "",
  color: "#e91e63",
  coordinates: [],
  tags: [],
  hidden: false,
});

const createEmptyArea = (): MapAreaFormValue => ({
  id: createId("area"),
  name: "",
  type: areaTypes[0].value,
  description: "",
  color: "#1976d2",
  coordinates: [],
  tags: [],
  hidden: false,
});

const createEmptyPoint = (): MapPointFormValue => ({
  id: createId("poi"),
  name: "",
  category: pointCategories[0].value,
  description: "",
  lat: undefined,
  lng: undefined,
  vendorId: undefined,
  stageId: undefined,
  iconName: undefined,
  iconScale: 1,
  tags: [],
  hidden: false,
});

const sanitizeNumber = (value: unknown) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return undefined;
};

const sanitizeCoordinates = (coords?: CoordinateFormValue[], minLength = 3) => {
  let normalized = coords && coords.length > 0 ? coords : [];

  normalized = normalized.map((coord) => ({
    lat: sanitizeNumber(coord.lat),
    lng: sanitizeNumber(coord.lng),
  }));

  while (normalized.length < minLength) {
    normalized.push({ lat: undefined, lng: undefined });
  }

  return normalized;
};

const getFieldErrorMessage = (error: unknown) => {
  if (typeof error === "object" && error !== null && "message" in error) {
    return (error as { message?: string }).message;
  }

  return undefined;
};

const parseInteractiveMapConfig = (
  configs?: Config[],
): InteractiveMapFormValues => {
  const storedValue = configs?.find(
    (item) => item.key === "INTERACTIVE_MAP_DATA",
  )?.value;

  if (!storedValue) {
    return emptyForm;
  }

  try {
    const parsed = JSON.parse(storedValue);
    const areas = Array.isArray(parsed?.areas) ? parsed.areas : [];
    const pois = Array.isArray(parsed?.pointsOfInterest)
      ? parsed.pointsOfInterest
      : [];
    const lines = Array.isArray(parsed?.lines) ? parsed.lines : [];

    return {
      areas: areas.map((area: any, index: number) => ({
        id: area.id ?? createId(`area-${index}`),
        name: area.name ?? "",
        type: area.type ?? areaTypes[0].value,
        description: area.description ?? "",
        color: area.color ?? "#1976d2",
        coordinates: sanitizeCoordinates(area.coordinates),
        tags: Array.isArray(area.tags)
          ? area.tags.filter((t: unknown) => typeof t === "string")
          : [],
        hidden: typeof area.hidden === "boolean" ? area.hidden : false,
      })),
      pointsOfInterest: pois.map((point: any, index: number) => ({
        id: point.id ?? createId(`poi-${index}`),
        name: point.name ?? "",
        category: point.category ?? pointCategories[0].value,
        description: point.description ?? "",
        lat: sanitizeNumber(point.lat ?? point.latitude),
        lng: sanitizeNumber(point.lng ?? point.longitude),
        vendorId:
          typeof point.vendorId === "string" ? point.vendorId : undefined,
        stageId: typeof point.stageId === "string" ? point.stageId : undefined,
        iconName:
          typeof point.iconName === "string" ? point.iconName : undefined,
        iconScale:
          typeof point.iconScale === "number" ? point.iconScale : undefined,
        tags: Array.isArray(point.tags)
          ? point.tags.filter((t: unknown) => typeof t === "string")
          : [],
        hidden: typeof point.hidden === "boolean" ? point.hidden : false,
      })),
      lines: lines.map((line: any, index: number) => ({
        id: line.id ?? createId(`line-${index}`),
        name: line.name ?? "",
        color: line.color ?? "#e91e63",
        coordinates: sanitizeCoordinates(line.coordinates, 2),
        tags: Array.isArray(line.tags)
          ? line.tags.filter((t: unknown) => typeof t === "string")
          : [],
        hidden: typeof line.hidden === "boolean" ? line.hidden : false,
      })),
    };
  } catch (error) {
    console.warn("Unable to parse INTERACTIVE_MAP_DATA", error);
    return emptyForm;
  }
};

// Returns a permanently stable function reference that always invokes the
// latest `fn`, so it can be passed to memoized children without ever
// invalidating them, while avoiding manual dependency-array bookkeeping.
function useEventCallback<Args extends unknown[], R>(
  fn: (...args: Args) => R,
): (...args: Args) => R {
  const ref = useRef(fn);
  ref.current = fn;
  return useRef((...args: Args) => ref.current(...args)).current;
}

// Delays propagating fast-changing values (e.g. form fields watched on every
// keystroke) so expensive consumers like the map preview only re-render a
// short while after typing settles, instead of on every character.
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

interface InteractiveMapEditorProps {
  tab: number;
  onTabChange: (tab: number) => void;
}

export const InteractiveMapEditor = ({ tab, onTabChange }: InteractiveMapEditorProps) => {
  const isSuperAdmin = useIsSuperAdmin();
  const currentUserEmail = useCurrentUserEmail();
  const { data, loading } = useQuery(GET_CONFIG);
  const { data: settingsData } = useQuery(GET_FESTIVAL_SETTINGS);
  const editionId = settingsData?.settings?.[0]?.editionId;
  const [updateConfig, { loading: saving }] = useMutation(UPDATE_CONFIG, {
    refetchQueries: ["config"],
    awaitRefetchQueries: true,
    onCompleted: () => toast("Interactive map saved"),
  });

  const configs = useMemo(() => (data?.configs as Config[]) ?? [], [data]);

  const canPrintMap = useMemo(() => {
    if (isSuperAdmin) return true;
    if (!currentUserEmail) return false;
    const allowed =
      configs.find((c) => c.key === "PRINT_MAP_ALLOWED_USERS")?.value ?? "";
    return allowed
      .split(",")
      .map((e: string) => e.trim().toLowerCase())
      .includes(currentUserEmail.toLowerCase());
  }, [isSuperAdmin, currentUserEmail, configs]);
  const parsedValues = useMemo(
    () => parseInteractiveMapConfig(configs),
    [configs],
  );

  const form = useForm<InteractiveMapFormValues>({
    defaultValues: parsedValues,
    resolver: yupResolver(formSchema) as Resolver<InteractiveMapFormValues>,
    mode: "onBlur",
  });

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
    setValue,
  } = form;

  const watchedAreas = useWatch({ control, name: "areas" });
  const watchedPoints = useWatch({ control, name: "pointsOfInterest" });
  const watchedLines = useWatch({ control, name: "lines" });
  // Debounced so the (potentially large) map preview doesn't rebuild every
  // Leaflet layer on every single keystroke while editing a field.
  const debouncedMapAreas = useDebouncedValue(watchedAreas, 150);
  const debouncedMapPoints = useDebouncedValue(watchedPoints, 150);
  const debouncedMapLines = useDebouncedValue(watchedLines, 150);
  const [mapPointCaptureEnabled, setMapPointCaptureEnabled] = useState(false);
  const [mapAreaCaptureEnabled, setMapAreaCaptureEnabled] = useState(false);
  const [mapLineCaptureEnabled, setMapLineCaptureEnabled] = useState(false);
  const [pointMoveTargetId, setPointMoveTargetId] = useState<string | null>(
    null,
  );
  const [mapFocusTarget, setMapFocusTarget] = useState<MapFocusTarget | null>(
    null,
  );
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null);
  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);
  const [coordinateUndoStack, setCoordinateUndoStack] = useState<
    CoordinateFormValue[][]
  >([]);
  const [lineCoordinateUndoStack, setLineCoordinateUndoStack] = useState<
    CoordinateFormValue[][]
  >([]);
  const [rolesSectionOpen, setRolesSectionOpen] = useState(false);
  const [areasSectionOpen, setAreasSectionOpen] = useState(true);
  const [pointsSectionOpen, setPointsSectionOpen] = useState(true);
  const [linesSectionOpen, setLinesSectionOpen] = useState(true);
  const [activeTab, setActiveTab] = useState<"areas" | "points" | "lines">(
    "areas",
  );
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const [areaAccordionOpenState, setAreaAccordionOpenState] = useState<
    Record<string, boolean>
  >({});
  const [poiAccordionOpenState, setPoiAccordionOpenState] = useState<
    Record<string, boolean>
  >({});
  const [lineAccordionOpenState, setLineAccordionOpenState] = useState<
    Record<string, boolean>
  >({});
  const [roleLinks, setRoleLinks] = useState<Record<string, string>>({});
  const [visibleCategories, setVisibleCategories] = useState<Set<string>>(
    () => new Set(pointCategories.map((c) => c.value)),
  );
  const [searchQuery, setSearchQuery] = useState("");

  const handleCloseMapDialog = useEventCallback(() => {
    setMapDialogOpen(false);
  });

  const handleCategoryChipClick = useEventCallback((value: string) => {
    setVisibleCategories((prev) => {
      const all = new Set(pointCategories.map((c) => c.value));
      if (prev.size === 1 && prev.has(value)) return all;
      if (prev.has(value)) return new Set([value]);
      const next = new Set(prev);
      next.add(value);
      return next;
    });
  });

  const handleResetCategories = useEventCallback(() =>
    setVisibleCategories(new Set(pointCategories.map((c) => c.value))),
  );

  const appRoles = useMemo(() => {
    const storedValue = configs.find((item) => item.key === "APP_ROLES")
      ?.value;
    if (!storedValue) return [];
    try {
      const parsed = JSON.parse(storedValue);
      return Array.isArray(parsed)
        ? parsed.filter((r): r is string => typeof r === "string")
        : [];
    } catch {
      return [];
    }
  }, [configs]);

  const distinctTags = useMemo(() => {
    const tags = new Set<string>();
    watchedAreas?.forEach((a) => a.tags?.forEach((t) => tags.add(t)));
    watchedPoints?.forEach((p) => p.tags?.forEach((t) => tags.add(t)));
    watchedLines?.forEach((l) => l.tags?.forEach((t) => tags.add(t)));
    appRoles.forEach((r) => tags.add(r));
    return Array.from(tags).sort();
  }, [watchedAreas, watchedPoints, watchedLines, appRoles]);

  const distinctTagsKey = distinctTags.join("\0");

  useEffect(() => {
    if (!editionId || !distinctTagsKey) return;
    const tags = distinctTagsKey.split("\0");
    let cancelled = false;
    Promise.all(
      tags.map(
        async (tag) => [tag, await getRoleDeeplink(tag, editionId)] as const,
      ),
    ).then((entries) => {
      if (!cancelled) setRoleLinks(Object.fromEntries(entries));
    });
    return () => {
      cancelled = true;
    };
  }, [distinctTagsKey, editionId]);

  const areaCoordinateAppendRefs = useRef<
    Record<string, (value: CoordinateFormValue) => void>
  >({});
  const lineCoordinateAppendRefs = useRef<
    Record<string, (value: CoordinateFormValue) => void>
  >({});

  useEffect(() => {
    if (loading) return;
    reset(parsedValues);
  }, [loading, parsedValues, reset]);

  useEffect(() => {
    if (!watchedAreas || watchedAreas.length === 0) {
      setSelectedAreaId(null);
      setMapAreaCaptureEnabled(false);
      return;
    }

    if (!selectedAreaId) {
      setSelectedAreaId(watchedAreas[0].id);
      return;
    }

    const stillExists = watchedAreas.some((area) => area.id === selectedAreaId);

    if (!stillExists) {
      setSelectedAreaId(watchedAreas[0]?.id ?? null);
      setMapAreaCaptureEnabled(false);
    }
  }, [watchedAreas, selectedAreaId]);

  useEffect(() => {
    setCoordinateUndoStack([]);
  }, [selectedAreaId]);

  useEffect(() => {
    if (!pointMoveTargetId) {
      return;
    }

    const pointStillExists = watchedPoints?.some(
      (point) => point.id === pointMoveTargetId,
    );

    if (!pointStillExists) {
      setPointMoveTargetId(null);
    }
  }, [watchedPoints, pointMoveTargetId]);

  const {
    fields: areaFields,
    append: appendArea,
    remove: removeArea,
  } = useFieldArray({
    control,
    name: "areas",
  });

  const {
    fields: poiFields,
    append: appendPoi,
    remove: removePoi,
  } = useFieldArray({
    control,
    name: "pointsOfInterest",
  });

  const {
    fields: lineFields,
    append: appendLine,
    remove: removeLine,
  } = useFieldArray({
    control,
    name: "lines",
  });

  useEffect(() => {
    setAreaAccordionOpenState((prev) => {
      const next: Record<string, boolean> = {};
      areaFields.forEach((field, index) => {
        const key = watchedAreas?.[index]?.id ?? field.id;
        next[key] = prev[key] ?? true;
      });
      return next;
    });
    // watchedAreas intentionally omitted — only need to re-run when field list changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaFields]);

  useEffect(() => {
    setPoiAccordionOpenState((prev) => {
      const next: Record<string, boolean> = {};
      poiFields.forEach((field, index) => {
        const key = watchedPoints?.[index]?.id ?? field.id;
        next[key] = prev[key] ?? true;
      });
      return next;
    });
    // watchedPoints intentionally omitted — only need to re-run when field list changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poiFields]);

  useEffect(() => {
    setLineAccordionOpenState((prev) => {
      const next: Record<string, boolean> = {};
      lineFields.forEach((field, index) => {
        const key = watchedLines?.[index]?.id ?? field.id;
        next[key] = prev[key] ?? true;
      });
      return next;
    });
    // watchedLines intentionally omitted — only need to re-run when field list changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineFields]);

  useEffect(() => {
    if (!watchedLines || watchedLines.length === 0) {
      setSelectedLineId(null);
      setMapLineCaptureEnabled(false);
      return;
    }
    if (!selectedLineId) {
      setSelectedLineId(watchedLines[0].id);
      return;
    }
    const stillExists = watchedLines.some((line) => line.id === selectedLineId);
    if (!stillExists) {
      setSelectedLineId(watchedLines[0]?.id ?? null);
      setMapLineCaptureEnabled(false);
    }
  }, [watchedLines, selectedLineId]);

  useEffect(() => {
    setLineCoordinateUndoStack([]);
  }, [selectedLineId]);

  const onSubmit = (values: InteractiveMapFormValues) => {
    const payload: InteractiveMapFormValues = {
      areas: values.areas.map((area) => ({
        ...area,
        coordinates: area.coordinates.map((coord) => ({
          lat: coord.lat,
          lng: coord.lng,
        })),
      })),
      pointsOfInterest: values.pointsOfInterest.map((poi) => ({
        ...poi,
        lat: poi.lat,
        lng: poi.lng,
        iconName: poi.iconName || null,
        iconScale: poi.iconScale ?? null,
      })),
      lines: values.lines.map((line) => ({
        ...line,
        coordinates: line.coordinates.map((coord) => ({
          lat: coord.lat,
          lng: coord.lng,
        })),
      })),
    };

    updateConfig({
      variables: {
        settings: {
          key: "INTERACTIVE_MAP_DATA",
          value: JSON.stringify(payload),
        },
      },
    });
  };

  const flattenedErrors = useMemo(() => {
    const messages: string[] = [];
    const areaErrs = errors.areas as any[] | undefined;
    areaErrs?.forEach((areaErr, i) => {
      if (!areaErr) return;
      const label = watchedAreas?.[i]?.name || `Area ${i + 1}`;
      (["name", "type", "color"] as const).forEach((f) => {
        if (areaErr[f]?.message)
          messages.push(`${label}: ${areaErr[f].message}`);
      });
      if (areaErr.coordinates?.message) {
        messages.push(`${label}: ${areaErr.coordinates.message}`);
      } else if (Array.isArray(areaErr.coordinates)) {
        areaErr.coordinates.forEach((ce: any, j: number) => {
          if (!ce) return;
          if (ce.lat?.message)
            messages.push(`${label} point ${j + 1}: ${ce.lat.message}`);
          if (ce.lng?.message)
            messages.push(`${label} point ${j + 1}: ${ce.lng.message}`);
        });
      }
    });
    const poiErrs = errors.pointsOfInterest as any[] | undefined;
    poiErrs?.forEach((poiErr, i) => {
      if (!poiErr) return;
      const label = watchedPoints?.[i]?.name || `Point ${i + 1}`;
      (
        ["name", "category", "lat", "lng", "vendorId", "stageId"] as const
      ).forEach((f) => {
        if (poiErr[f]?.message) messages.push(`${label}: ${poiErr[f].message}`);
      });
    });
    const lineErrs = errors.lines as any[] | undefined;
    lineErrs?.forEach((lineErr, i) => {
      if (!lineErr) return;
      const label = watchedLines?.[i]?.name || `Line ${i + 1}`;
      (["name", "color"] as const).forEach((f) => {
        if (lineErr[f]?.message)
          messages.push(`${label}: ${lineErr[f].message}`);
      });
      if (lineErr.coordinates?.message) {
        messages.push(`${label}: ${lineErr.coordinates.message}`);
      } else if (Array.isArray(lineErr.coordinates)) {
        lineErr.coordinates.forEach((ce: any, j: number) => {
          if (!ce) return;
          if (ce.lat?.message)
            messages.push(`${label} point ${j + 1}: ${ce.lat.message}`);
          if (ce.lng?.message)
            messages.push(`${label} point ${j + 1}: ${ce.lng.message}`);
        });
      }
    });
    return messages;
  }, [errors, watchedAreas, watchedPoints, watchedLines]);

  const onInvalid = () => {
    setShowValidationErrors(true);
    toast.error("Save failed — fix the errors listed below");
  };

  const handleMapPointAdd = useEventCallback(
    ({ lat, lng }: { lat: number; lng: number }) => {
      appendPoi({
        ...createEmptyPoint(),
        name: "New point",
        lat,
        lng,
      });
      toast(`Added point at ${lat.toFixed(4)}, ${lng.toFixed(4)}`);
      setMapPointCaptureEnabled(false);
    },
  );

  const handlePointMoveStart = useEventCallback((pointId: string) => {
    const points = watch("pointsOfInterest") ?? [];
    const point = points.find((entry) => entry.id === pointId);

    if (!point) {
      toast("Selected point no longer exists");
      return;
    }

    setMapAreaCaptureEnabled(false);
    setMapPointCaptureEnabled(false);
    setPointMoveTargetId(pointId);
    setActiveTab("points");
    toast(
      `Editing ${point.name || "point"}: click the map to place the new location.`,
    );
  });

  const handlePointMoveCancel = useEventCallback(() => {
    setPointMoveTargetId(null);
  });

  const handlePointMoveComplete = useEventCallback(
    ({ lat, lng }: { lat: number; lng: number }) => {
      if (!pointMoveTargetId) {
        return;
      }

      const points = watch("pointsOfInterest") ?? [];
      const targetIndex = points.findIndex(
        (point) => point.id === pointMoveTargetId,
      );

      if (targetIndex === -1) {
        toast("Selected point no longer exists");
        setPointMoveTargetId(null);
        return;
      }

      setValue(`pointsOfInterest.${targetIndex}.lat`, lat, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      setValue(`pointsOfInterest.${targetIndex}.lng`, lng, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });

      const targetName = points[targetIndex]?.name || "point";
      setPointMoveTargetId(null);
      toast(`Moved ${targetName} to ${lat.toFixed(4)}, ${lng.toFixed(4)}`);
    },
  );

  const handleZoomToPointInPreview = useEventCallback((pointIndex: number) => {
    const points = watch("pointsOfInterest") ?? [];
    const point = points[pointIndex];
    const lat = sanitizeNumber(point?.lat);
    const lng = sanitizeNumber(point?.lng);

    if (!point || lat === undefined || lng === undefined) {
      toast("Add latitude and longitude to this point before zooming.");
      return;
    }

    setMapAreaCaptureEnabled(false);
    setMapPointCaptureEnabled(false);
    setPointMoveTargetId(null);
    setActiveTab("points");
    setMapFocusTarget({ lat, lng });
  });

  const pushUndoSnapshot = (areaId: string) => {
    const areas = watch("areas") ?? [];
    const targetIndex = areas.findIndex((area) => area.id === areaId);
    if (targetIndex === -1) return;
    const snapshot = (areas[targetIndex]?.coordinates ?? []).map((c) => ({
      ...c,
    }));
    setCoordinateUndoStack((prev) => [...prev, snapshot]);
  };

  const handleMapAreaPointAdd = useEventCallback(({
    lat,
    lng,
  }: {
    lat: number;
    lng: number;
  }) => {
    if (!selectedAreaId) {
      toast("Select an area before capturing points");
      setMapAreaCaptureEnabled(false);
      return;
    }

    pushUndoSnapshot(selectedAreaId);

    const appendFn = areaCoordinateAppendRefs.current[selectedAreaId];
    const areas = watch("areas") ?? [];
    const targetIndex = areas.findIndex((area) => area.id === selectedAreaId);

    if (appendFn) {
      appendFn({ lat, lng });
    } else if (targetIndex !== -1) {
      const nextCoords = [
        ...(areas[targetIndex]?.coordinates ?? []),
        { lat, lng },
      ];
      setValue(`areas.${targetIndex}.coordinates`, nextCoords, {
        shouldDirty: true,
        shouldTouch: true,
      });
    } else {
      toast("Selected area no longer exists");
      setMapAreaCaptureEnabled(false);
      return;
    }

    const areaName =
      areas[targetIndex]?.name ??
      watchedAreas?.find((area) => area.id === selectedAreaId)?.name ??
      "area";

    toast(
      `Added coordinate to ${areaName} (${lat.toFixed(4)}, ${lng.toFixed(4)})`,
    );
  });

  const handleToggleAreaCapture = useEventCallback(() => {
    if (!selectedAreaId) {
      toast("Select an area first");
      return;
    }
    setPointMoveTargetId(null);
    setMapPointCaptureEnabled(false);
    setMapLineCaptureEnabled(false);
    setMapAreaCaptureEnabled((prev) => !prev);
  });

  const handleTogglePointCapture = useEventCallback(() => {
    setPointMoveTargetId(null);
    setMapAreaCaptureEnabled(false);
    setMapLineCaptureEnabled(false);
    setMapPointCaptureEnabled((prev) => !prev);
  });

  const handleToggleLineCapture = useEventCallback(() => {
    if (!selectedLineId) {
      toast("Select a line first");
      return;
    }
    setPointMoveTargetId(null);
    setMapPointCaptureEnabled(false);
    setMapAreaCaptureEnabled(false);
    setMapLineCaptureEnabled((prev) => !prev);
  });

  const handleEditorTabChange = (
    _: SyntheticEvent,
    value: "areas" | "points" | "lines",
  ) => {
    setActiveTab(value);
    if (value !== "areas") {
      setMapAreaCaptureEnabled(false);
    }
  };

  const handleUndoLastAreaPoint = useEventCallback(() => {
    if (!selectedAreaId) {
      toast("Select an area first");
      return;
    }

    if (coordinateUndoStack.length === 0) {
      toast("Nothing to undo");
      return;
    }

    const areas = watch("areas") ?? [];
    const targetIndex = areas.findIndex((area) => area.id === selectedAreaId);

    if (targetIndex === -1) {
      toast("Selected area no longer exists");
      setMapAreaCaptureEnabled(false);
      return;
    }

    const previousCoords = coordinateUndoStack[coordinateUndoStack.length - 1];
    setCoordinateUndoStack((prev) => prev.slice(0, -1));
    setValue(`areas.${targetIndex}.coordinates`, previousCoords, {
      shouldDirty: true,
      shouldTouch: true,
    });
  });

  const handleMoveAreaCoordinate = useEventCallback(({
    areaId,
    coordIndex,
    lat,
    lng,
  }: {
    areaId: string;
    coordIndex: number;
    lat: number;
    lng: number;
  }) => {
    pushUndoSnapshot(areaId);
    const areas = watch("areas") ?? [];
    const targetIndex = areas.findIndex((area) => area.id === areaId);
    if (targetIndex === -1) return;
    setValue(`areas.${targetIndex}.coordinates.${coordIndex}.lat`, lat, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    setValue(`areas.${targetIndex}.coordinates.${coordIndex}.lng`, lng, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  });

  const handleMovePoi = useEventCallback(({
    pointId,
    lat,
    lng,
  }: {
    pointId: string;
    lat: number;
    lng: number;
  }) => {
    const points = watch("pointsOfInterest") ?? [];
    const targetIndex = points.findIndex((p) => p.id === pointId);
    if (targetIndex === -1) return;
    setValue(`pointsOfInterest.${targetIndex}.lat`, lat, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    setValue(`pointsOfInterest.${targetIndex}.lng`, lng, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  });

  const pushLineUndoSnapshot = (lineId: string) => {
    const lines = watch("lines") ?? [];
    const targetIndex = lines.findIndex((line) => line.id === lineId);
    if (targetIndex === -1) return;
    const snapshot = (lines[targetIndex]?.coordinates ?? []).map((c) => ({
      ...c,
    }));
    setLineCoordinateUndoStack((prev) => [...prev, snapshot]);
  };

  const handleMapLinePointAdd = useEventCallback(({
    lat,
    lng,
  }: {
    lat: number;
    lng: number;
  }) => {
    if (!selectedLineId) {
      toast("Select a line before capturing points");
      setMapLineCaptureEnabled(false);
      return;
    }

    pushLineUndoSnapshot(selectedLineId);

    const appendFn = lineCoordinateAppendRefs.current[selectedLineId];
    const lines = watch("lines") ?? [];
    const targetIndex = lines.findIndex((line) => line.id === selectedLineId);

    if (appendFn) {
      appendFn({ lat, lng });
    } else if (targetIndex !== -1) {
      const nextCoords = [
        ...(lines[targetIndex]?.coordinates ?? []),
        { lat, lng },
      ];
      setValue(`lines.${targetIndex}.coordinates`, nextCoords, {
        shouldDirty: true,
        shouldTouch: true,
      });
    } else {
      toast("Selected line no longer exists");
      setMapLineCaptureEnabled(false);
      return;
    }

    const lineName =
      lines[targetIndex]?.name ??
      watchedLines?.find((line) => line.id === selectedLineId)?.name ??
      "line";
    toast(
      `Added coordinate to ${lineName} (${lat.toFixed(4)}, ${lng.toFixed(4)})`,
    );
  });

  const handleUndoLastLinePoint = useEventCallback(() => {
    if (!selectedLineId) {
      toast("Select a line first");
      return;
    }
    if (lineCoordinateUndoStack.length === 0) {
      toast("Nothing to undo");
      return;
    }
    const lines = watch("lines") ?? [];
    const targetIndex = lines.findIndex((line) => line.id === selectedLineId);
    if (targetIndex === -1) {
      toast("Selected line no longer exists");
      setMapLineCaptureEnabled(false);
      return;
    }
    const previousCoords =
      lineCoordinateUndoStack[lineCoordinateUndoStack.length - 1];
    setLineCoordinateUndoStack((prev) => prev.slice(0, -1));
    setValue(`lines.${targetIndex}.coordinates`, previousCoords, {
      shouldDirty: true,
      shouldTouch: true,
    });
  });

  const handleMoveLineCoordinate = useEventCallback(({
    lineId,
    coordIndex,
    lat,
    lng,
  }: {
    lineId: string;
    coordIndex: number;
    lat: number;
    lng: number;
  }) => {
    pushLineUndoSnapshot(lineId);
    const lines = watch("lines") ?? [];
    const targetIndex = lines.findIndex((line) => line.id === lineId);
    if (targetIndex === -1) return;
    setValue(`lines.${targetIndex}.coordinates.${coordIndex}.lat`, lat, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    setValue(`lines.${targetIndex}.coordinates.${coordIndex}.lng`, lng, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  });

  const handleOpenAreaMap = useEventCallback((areaId: string) => {
    setSelectedAreaId(areaId);
    setMapPointCaptureEnabled(false);
    setPointMoveTargetId(null);
    setMapLineCaptureEnabled(false);
    setMapAreaCaptureEnabled(true);
    setActiveTab("areas");
    setMapDialogOpen(true);
  });

  const handleOpenLineMap = useEventCallback((lineId: string) => {
    setSelectedLineId(lineId);
    setMapPointCaptureEnabled(false);
    setPointMoveTargetId(null);
    setMapAreaCaptureEnabled(false);
    setMapLineCaptureEnabled(true);
    setActiveTab("lines");
    setMapDialogOpen(true);
  });

  const handleOpenPointMap = useEventCallback((pointId: string) => {
    const points = watch("pointsOfInterest") ?? [];
    const point = points.find((entry) => entry.id === pointId);
    if (!point) {
      toast("Selected point no longer exists");
      return;
    }
    setMapAreaCaptureEnabled(false);
    setMapLineCaptureEnabled(false);
    setMapPointCaptureEnabled(false);
    setPointMoveTargetId(pointId);
    if (typeof point.lat === "number" && typeof point.lng === "number") {
      setMapFocusTarget({ lat: point.lat, lng: point.lng });
    }
    setActiveTab("points");
    setMapDialogOpen(true);
  });

  const handleRegisterAreaHelpers = useEventCallback(
    (
      areaId: string,
      helpers: { appendCoordinate: (value: CoordinateFormValue) => void },
    ) => {
      areaCoordinateAppendRefs.current[areaId] = helpers.appendCoordinate;
    },
  );
  const handleUnregisterAreaHelpers = useEventCallback((areaId: string) => {
    delete areaCoordinateAppendRefs.current[areaId];
  });
  const handleToggleAreaAccordion = useEventCallback(
    (key: string, open: boolean) => {
      setAreaAccordionOpenState((prev) => ({ ...prev, [key]: open }));
    },
  );

  const handleRegisterLineHelpers = useEventCallback(
    (
      lineId: string,
      helpers: { appendCoordinate: (value: CoordinateFormValue) => void },
    ) => {
      lineCoordinateAppendRefs.current[lineId] = helpers.appendCoordinate;
    },
  );
  const handleUnregisterLineHelpers = useEventCallback((lineId: string) => {
    delete lineCoordinateAppendRefs.current[lineId];
  });
  const handleToggleLineAccordion = useEventCallback(
    (key: string, open: boolean) => {
      setLineAccordionOpenState((prev) => ({ ...prev, [key]: open }));
    },
  );

  const handleTogglePoiAccordion = useEventCallback(
    (key: string, open: boolean) => {
      setPoiAccordionOpenState((prev) => ({ ...prev, [key]: open }));
    },
  );

  const canUndoAreaPoint = coordinateUndoStack.length > 0 && !!selectedAreaId;
  const canUndoLinePoint =
    lineCoordinateUndoStack.length > 0 && !!selectedLineId;

  return (
    <PageContainer
      title="Interactive Map Data"
      actions={
        <>
          <UpdateMiscConfigButton />
          {canPrintMap && (
            <Button
              variant="outlined"
              onClick={() => window.open("/map/print", "_blank")}
            >
              Print map
            </Button>
          )}
          <Button
            variant="outlined"
            disabled={loading || saving}
            onClick={() => reset(parsedValues)}
          >
            Reset changes
          </Button>
          <Button
            type="submit"
            form="interactive-map-form"
            variant="contained"
            disabled={loading || saving}
          >
            {saving ? "Saving…" : "Save map"}
          </Button>
        </>
      }
    >
      <MapTabs value={tab} onChange={onTabChange} />
      <LoadingOverlay loading={loading}>
        <Grid container spacing={4}>
          <Grid item xs={12} xl={8}>
            <Accordion
              expanded={rolesSectionOpen}
              onChange={(_, open) => setRolesSectionOpen(open)}
              sx={{ mb: 3 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Roles</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {distinctTags.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No tags defined yet.
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {distinctTags.map((tag) => {
                      const url = roleLinks[tag];
                      const code = url?.split("://role/")[1];
                      return (
                        <Box
                          key={tag}
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ minWidth: 120, fontWeight: 500 }}
                          >
                            {tag}
                          </Typography>
                          {url ? (
                            <>
                              <Link
                                href={url}
                                underline="hover"
                                variant="body2"
                                sx={{ flex: 1, wordBreak: "break-all" }}
                              >
                                {url}
                              </Link>
                              <IconButton
                                size="small"
                                title="Copy full URL"
                                onClick={() =>
                                  navigator.clipboard.writeText(url)
                                }
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                title="Copy code"
                                onClick={() =>
                                  navigator.clipboard.writeText(code ?? "")
                                }
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {editionId
                                ? "Generating…"
                                : "No edition ID configured"}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </AccordionDetails>
            </Accordion>
            <form
              id="interactive-map-form"
              onSubmit={handleSubmit(onSubmit, onInvalid)}
            >
              <Collapse in={showValidationErrors && flattenedErrors.length > 0}>
                <Alert
                  severity="error"
                  onClose={() => setShowValidationErrors(false)}
                  sx={{ mb: 3 }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    {flattenedErrors.length} validation{" "}
                    {flattenedErrors.length === 1 ? "error" : "errors"} — fix
                    these before saving:
                  </Typography>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {flattenedErrors.map((msg, i) => (
                      <li key={i}>
                        <Typography variant="body2">{msg}</Typography>
                      </li>
                    ))}
                  </ul>
                </Alert>
              </Collapse>
              <Typography variant="body1" sx={{ mb: 3 }}>
                Define the areas and points of interest that power the
                interactive map. Areas should include at least three coordinates
                to draw a polygon, and each point should include a latitude and
                longitude.
              </Typography>
              <TextField
                size="small"
                placeholder="Search by name…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ mb: 2 }}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchQuery("")}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
              <Tabs
                value={activeTab}
                onChange={handleEditorTabChange}
                sx={{ mb: 2 }}
                variant="fullWidth"
              >
                <Tab label="Points of interest" value="points" />
                <Tab label="Areas" value="areas" />
                <Tab label="Lines" value="lines" />
              </Tabs>

              <Box sx={{ display: activeTab === "areas" ? "block" : "none" }}>
                <Accordion
                  expanded={areasSectionOpen}
                  onChange={(_, open) => setAreasSectionOpen(open)}
                  sx={{ mb: 3 }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Areas</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mb: 1,
                      }}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          const shouldExpand =
                            areaFields.length === 0
                              ? true
                              : !areaFields.every((field, i) => {
                                  const k = watchedAreas?.[i]?.id ?? field.id;
                                  return areaAccordionOpenState[k];
                                });
                          setAreaAccordionOpenState(() =>
                            areaFields.reduce(
                              (acc, field, i) => {
                                const k = watchedAreas?.[i]?.id ?? field.id;
                                return { ...acc, [k]: shouldExpand };
                              },
                              {} as Record<string, boolean>,
                            ),
                          );
                        }}
                      >
                        {areaFields.length > 0 &&
                        areaFields.every((field, i) => {
                          const k = watchedAreas?.[i]?.id ?? field.id;
                          return areaAccordionOpenState[k];
                        })
                          ? "Collapse all"
                          : "Expand all"}
                      </Button>
                    </Box>
                    {areaFields.length === 0 && (
                      <EmptyState message="No areas yet. Add camping fields, arenas, or other regions." />
                    )}
                    {areaFields.map((field, index) => {
                      const areaKey = watchedAreas?.[index]?.id ?? field.id;
                      const areaName = (
                        watchedAreas?.[index]?.name ??
                        field.name ??
                        ""
                      ).toLowerCase();
                      if (
                        searchQuery &&
                        !areaName.includes(searchQuery.toLowerCase())
                      )
                        return null;
                      return (
                        <AreaEditor
                          key={field.id}
                          index={index}
                          areaKey={areaKey}
                          control={control}
                          register={register}
                          field={field}
                          errors={errors}
                          onRemove={removeArea}
                          onFocusArea={setSelectedAreaId}
                          onRegisterAreaHelpers={handleRegisterAreaHelpers}
                          onUnregisterAreaHelpers={handleUnregisterAreaHelpers}
                          setValue={setValue}
                          expanded={areaAccordionOpenState[areaKey] ?? false}
                          onToggleExpand={handleToggleAreaAccordion}
                          onOpenAreaMap={handleOpenAreaMap}
                          watchedColor={
                            watchedAreas?.[index]?.color ?? field.color
                          }
                          watchedHidden={watchedAreas?.[index]?.hidden ?? false}
                        />
                      );
                    })}
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="outlined"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={() => appendArea(createEmptyArea())}
                      >
                        Add area
                      </Button>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>

              <Box sx={{ display: activeTab === "points" ? "block" : "none" }}>
                <Accordion
                  expanded={pointsSectionOpen}
                  onChange={(_, open) => setPointsSectionOpen(open)}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Points of interest</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mb: 1,
                      }}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          const shouldExpand =
                            poiFields.length === 0
                              ? true
                              : !poiFields.every((field, i) => {
                                  const k = watchedPoints?.[i]?.id ?? field.id;
                                  return poiAccordionOpenState[k];
                                });
                          setPoiAccordionOpenState(() =>
                            poiFields.reduce(
                              (acc, field, i) => {
                                const k = watchedPoints?.[i]?.id ?? field.id;
                                return { ...acc, [k]: shouldExpand };
                              },
                              {} as Record<string, boolean>,
                            ),
                          );
                        }}
                      >
                        {poiFields.length > 0 &&
                        poiFields.every((field, i) => {
                          const k = watchedPoints?.[i]?.id ?? field.id;
                          return poiAccordionOpenState[k];
                        })
                          ? "Collapse all"
                          : "Expand all"}
                      </Button>
                    </Box>
                    {poiFields.length === 0 && (
                      <EmptyState message="Add stages, food vendors, and other markers." />
                    )}

                    {poiFields.length > 0 &&
                      (() => {
                        const presentCategories = pointCategories.filter(
                          (cat) =>
                            watchedPoints?.some(
                              (p) => p.category === cat.value,
                            ),
                        );
                        if (presentCategories.length < 2) return null;
                        const allVisible = presentCategories.every((cat) =>
                          visibleCategories.has(cat.value),
                        );
                        return (
                          <Box sx={{ mb: 2 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                mb: 0.75,
                              }}
                            >
                              <FilterListIcon fontSize="small" color="action" />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={600}
                              >
                                Filter by category
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {presentCategories.map((cat) => {
                                const on = visibleCategories.has(cat.value);
                                return (
                                  <Chip
                                    key={cat.value}
                                    label={cat.label}
                                    size="small"
                                    variant={on ? "filled" : "outlined"}
                                    color={on ? "primary" : "default"}
                                    onClick={() =>
                                      handleCategoryChipClick(cat.value)
                                    }
                                  />
                                );
                              })}
                              {!allVisible && (
                                <Chip
                                  label="Show all"
                                  size="small"
                                  variant="outlined"
                                  onClick={handleResetCategories}
                                />
                              )}
                            </Box>
                          </Box>
                        );
                      })()}

                    {poiFields.map((field, index) => {
                      const poiKey = watchedPoints?.[index]?.id ?? field.id;
                      const pointCategory =
                        watchedPoints?.[index]?.category ?? "";
                      if (
                        pointCategory &&
                        !visibleCategories.has(pointCategory)
                      )
                        return null;
                      const poiName = (
                        watchedPoints?.[index]?.name ??
                        field.name ??
                        ""
                      ).toLowerCase();
                      if (
                        searchQuery &&
                        !poiName.includes(searchQuery.toLowerCase())
                      )
                        return null;
                      return (
                        <PointEditor
                          key={field.id}
                          index={index}
                          poiKey={poiKey}
                          register={register}
                          control={control}
                          setValue={setValue}
                          field={field}
                          errors={errors}
                          onRemove={removePoi}
                          onZoomToPoint={handleZoomToPointInPreview}
                          onOpenPointMap={handleOpenPointMap}
                          expanded={poiAccordionOpenState[poiKey] ?? false}
                          onToggleExpand={handleTogglePoiAccordion}
                        />
                      );
                    })}

                    <Button
                      variant="outlined"
                      startIcon={<AddCircleOutlineIcon />}
                      onClick={() => appendPoi(createEmptyPoint())}
                      sx={{ mt: 2 }}
                    >
                      Add point
                    </Button>
                  </AccordionDetails>
                </Accordion>
              </Box>

              <Box sx={{ display: activeTab === "lines" ? "block" : "none" }}>
                <Accordion
                  expanded={linesSectionOpen}
                  onChange={(_, open) => setLinesSectionOpen(open)}
                  sx={{ mb: 3 }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Lines</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mb: 1,
                      }}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          const shouldExpand =
                            lineFields.length === 0
                              ? true
                              : !lineFields.every((field, i) => {
                                  const k = watchedLines?.[i]?.id ?? field.id;
                                  return lineAccordionOpenState[k];
                                });
                          setLineAccordionOpenState(() =>
                            lineFields.reduce(
                              (acc, field, i) => {
                                const k = watchedLines?.[i]?.id ?? field.id;
                                return { ...acc, [k]: shouldExpand };
                              },
                              {} as Record<string, boolean>,
                            ),
                          );
                        }}
                      >
                        {lineFields.length > 0 &&
                        lineFields.every((field, i) => {
                          const k = watchedLines?.[i]?.id ?? field.id;
                          return lineAccordionOpenState[k];
                        })
                          ? "Collapse all"
                          : "Expand all"}
                      </Button>
                    </Box>
                    {lineFields.length === 0 && (
                      <EmptyState message="No lines yet. Lines are open paths defined by a series of coordinates." />
                    )}
                    {lineFields.map((field, index) => {
                      const lineKey = watchedLines?.[index]?.id ?? field.id;
                      const lineName = (
                        watchedLines?.[index]?.name ??
                        field.name ??
                        ""
                      ).toLowerCase();
                      if (
                        searchQuery &&
                        !lineName.includes(searchQuery.toLowerCase())
                      )
                        return null;
                      return (
                        <LineEditor
                          key={field.id}
                          index={index}
                          lineKey={lineKey}
                          control={control}
                          register={register}
                          field={field}
                          errors={errors}
                          onRemove={removeLine}
                          onFocusLine={setSelectedLineId}
                          onRegisterLineHelpers={handleRegisterLineHelpers}
                          onUnregisterLineHelpers={handleUnregisterLineHelpers}
                          setValue={setValue}
                          expanded={lineAccordionOpenState[lineKey] ?? false}
                          onToggleExpand={handleToggleLineAccordion}
                          onOpenLineMap={handleOpenLineMap}
                        />
                      );
                    })}
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="outlined"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={() => appendLine(createEmptyLine())}
                      >
                        Add line
                      </Button>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>
            </form>
          </Grid>
          <Grid item xs={12} xl={4}>
            <MapPreview
              areas={debouncedMapAreas}
              points={debouncedMapPoints}
              lines={debouncedMapLines}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              pointCaptureEnabled={mapPointCaptureEnabled}
              onTogglePointCapture={handleTogglePointCapture}
              onAddPointFromMap={handleMapPointAdd}
              areaCaptureEnabled={mapAreaCaptureEnabled}
              onToggleAreaCapture={handleToggleAreaCapture}
              onAddAreaPointFromMap={handleMapAreaPointAdd}
              selectedAreaId={selectedAreaId}
              onSelectArea={setSelectedAreaId}
              onUndoLastAreaPoint={handleUndoLastAreaPoint}
              canUndoAreaPoint={canUndoAreaPoint}
              lineCaptureEnabled={mapLineCaptureEnabled}
              onToggleLineCapture={handleToggleLineCapture}
              onAddLinePointFromMap={handleMapLinePointAdd}
              selectedLineId={selectedLineId}
              onSelectLine={setSelectedLineId}
              onUndoLastLinePoint={handleUndoLastLinePoint}
              canUndoLinePoint={canUndoLinePoint}
              onMoveLineCoordinate={handleMoveLineCoordinate}
              pointMoveTargetId={pointMoveTargetId}
              onSelectPointForMove={handlePointMoveStart}
              onMovePointFromMap={handlePointMoveComplete}
              onCancelPointMove={handlePointMoveCancel}
              focusTarget={mapFocusTarget}
              expandedMapOpen={mapDialogOpen}
              onExpandedMapClose={handleCloseMapDialog}
              onMoveAreaCoordinate={handleMoveAreaCoordinate}
              onMovePoi={handleMovePoi}
              visibleCategories={visibleCategories}
              onCategoryChipClick={handleCategoryChipClick}
              onResetCategories={handleResetCategories}
            />
          </Grid>
        </Grid>
      </LoadingOverlay>
    </PageContainer>
  );
};

const EmptyState = ({ message }: { message: string }) => (
  <Paper
    elevation={0}
    sx={{
      border: (theme) => `1px dashed ${theme.palette.divider}`,
      p: 3,
      mb: 3,
      textAlign: "center",
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {message}
    </Typography>
  </Paper>
);

const TagsInput = ({
  value,
  onChange,
}: {
  value: string[];
  onChange: (next: string[]) => void;
}) => {
  const [input, setInput] = useState("");

  const add = (raw: string) => {
    const next = raw
      .split(/[\n,;]+/)
      .map((t) => t.trim())
      .filter(Boolean);
    if (next.length === 0) return;
    const seen = new Set(value.map((t) => t.toLowerCase()));
    const merged = [
      ...value,
      ...next.filter((t) => !seen.has(t.toLowerCase())),
    ];
    onChange(merged);
    setInput("");
  };

  return (
    <Stack spacing={0.5}>
      <TextField
        label="Tags"
        placeholder="e.g. VIP, staff"
        InputLabelProps={{ shrink: true }}
        value={input}
        fullWidth
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== "Enter") return;
          e.preventDefault();
          add(input);
        }}
        helperText="Press Enter to add a tag"
      />
      {value.length > 0 && (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {value.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              variant="outlined"
              onDelete={() => onChange(value.filter((t) => t !== tag))}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};

const AreaEditor = memo(
  ({
    index,
    areaKey,
    control,
    register,
    errors,
    onRemove,
    field,
    onFocusArea,
    onRegisterAreaHelpers,
    onUnregisterAreaHelpers,
    setValue,
    expanded,
    onToggleExpand,
    onOpenAreaMap,
    watchedColor,
    watchedHidden,
  }: {
    index: number;
    areaKey: string;
    control: Control<InteractiveMapFormValues>;
    register: UseFormRegister<InteractiveMapFormValues>;
    errors: FieldErrors<InteractiveMapFormValues>;
    onRemove: (index: number) => void;
    field: FieldArrayWithId<InteractiveMapFormValues, "areas", "id">;
    onFocusArea: (areaKey: string) => void;
    onRegisterAreaHelpers?: (
      areaId: string,
      helpers: { appendCoordinate: (value: CoordinateFormValue) => void },
    ) => void;
    onUnregisterAreaHelpers?: (areaId: string) => void;
    setValue: UseFormSetValue<InteractiveMapFormValues>;
    expanded: boolean;
    onToggleExpand: (areaKey: string, open: boolean) => void;
    onOpenAreaMap?: (areaKey: string) => void;
    watchedColor: string;
    watchedHidden: boolean;
  }) => {
    const {
      fields: coordinateFields,
      append: appendCoordinate,
      remove: removeCoordinate,
      move: moveCoordinate,
    } = useFieldArray({
      control,
      name: `areas.${index}.coordinates`,
    });
    const [dragSrcIndex, setDragSrcIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

    const areaErrors = errors?.areas?.[index];
    const nameError = getFieldErrorMessage(areaErrors?.name);
    const typeError = getFieldErrorMessage(areaErrors?.type);
    const colorError = getFieldErrorMessage(areaErrors?.color);
    const [isFetchingLocation, setIsFetchingLocation] = useState(false);
    const watchedAreaColor = watchedColor ?? field.color;
    const isHidden = watchedHidden;

    const colorInputRef = useRef<HTMLInputElement | null>(null);
    const {
      ref: colorRegRef,
      onChange: _colorOnChange,
      ...colorRegProps
    } = register(`areas.${index}.color` as const);
    useEffect(() => {
      const input = colorInputRef.current;
      if (!input) return;
      const handler = (e: Event) => {
        setValue(`areas.${index}.color`, (e.target as HTMLInputElement).value, {
          shouldDirty: true,
          shouldValidate: false,
        });
      };
      input.addEventListener("change", handler);
      return () => input.removeEventListener("change", handler);
    }, [index, setValue]);

    useEffect(() => {
      onRegisterAreaHelpers?.(field.id, { appendCoordinate });
      return () => {
        onUnregisterAreaHelpers?.(field.id);
      };
    }, [
      appendCoordinate,
      field.id,
      onRegisterAreaHelpers,
      onUnregisterAreaHelpers,
    ]);

    const focusThisArea = () => onFocusArea(areaKey);

    const handleUseCurrentLocation = (coordIndex: number) => {
      if (!navigator?.geolocation) {
        toast("Geolocation is not supported in this browser");
        return;
      }

      setIsFetchingLocation(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setIsFetchingLocation(false);
          setValue(
            `areas.${index}.coordinates.${coordIndex}.lat`,
            position.coords.latitude,
            { shouldDirty: true, shouldTouch: true },
          );
          setValue(
            `areas.${index}.coordinates.${coordIndex}.lng`,
            position.coords.longitude,
            { shouldDirty: true, shouldTouch: true },
          );
        },
        () => {
          setIsFetchingLocation(false);
          toast("Unable to retrieve your location");
        },
        {
          enableHighAccuracy: true,
        },
      );
    };

    return (
      <Accordion
        expanded={expanded}
        onChange={(_, open) => onToggleExpand(areaKey, open)}
        sx={{ mb: 2 }}
        disableGutters
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          onClick={focusThisArea}
          sx={{ px: 2 }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              gap: 2,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {field.name || `Area ${index + 1}`}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {coordinateFields.length > 0 && (
                <IconButton
                  aria-label="Edit area on expanded map"
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpenAreaMap?.(areaKey);
                  }}
                >
                  <OpenInFullIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton
                aria-label={isHidden ? "Show on app" : "Hide from app"}
                onClick={(event) => {
                  event.stopPropagation();
                  setValue(`areas.${index}.hidden`, !isHidden, {
                    shouldDirty: true,
                  });
                }}
                color={isHidden ? "warning" : "default"}
              >
                {isHidden ? (
                  <VisibilityOffIcon fontSize="small" />
                ) : (
                  <VisibilityIcon fontSize="small" />
                )}
              </IconButton>
              <IconButton
                aria-label="Remove area"
                onClick={(event) => {
                  event.stopPropagation();
                  const areaLabel = field.name || `Area ${index + 1}`;
                  confirmAlert({
                    title: "Delete this area?",
                    message: `Delete ${areaLabel}? Are you sure? This can't be undone`,
                    buttons: [
                      { label: "Yes", onClick: () => onRemove(index) },
                      { label: "No", onClick: () => {} },
                    ],
                  });
                }}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Name"
                InputLabelProps={{ shrink: true }}
                error={!!nameError}
                helperText={nameError}
                defaultValue={field.name}
                onFocus={focusThisArea}
                {...register(`areas.${index}.name` as const)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Type"
                InputLabelProps={{ shrink: true }}
                error={!!typeError}
                helperText={typeError}
                defaultValue={field.type ?? areaTypes[0].value}
                onFocus={focusThisArea}
                {...register(`areas.${index}.type` as const)}
              >
                {areaTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                type="color"
                label="Color"
                InputLabelProps={{ shrink: true }}
                error={!!colorError}
                helperText={colorError}
                defaultValue={field.color}
                onFocus={focusThisArea}
                {...colorRegProps}
                inputRef={(el) => {
                  colorInputRef.current = el;
                  colorRegRef(el);
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Description"
                InputLabelProps={{ shrink: true }}
                defaultValue={field.description ?? ""}
                onFocus={focusThisArea}
                {...register(`areas.${index}.description` as const)}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name={`areas.${index}.tags` as const}
                render={({ field: tagsField }) => (
                  <TagsInput
                    value={tagsField.value ?? []}
                    onChange={tagsField.onChange}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
            Coordinates
          </Typography>

          {coordinateFields.map((coordField, coordIndex) => {
            const coordErrors = areaErrors?.coordinates as
              | FieldErrors<CoordinateFormValue>[]
              | undefined;
            const coordError = coordErrors?.[coordIndex];
            const latMessage = getFieldErrorMessage(coordError?.lat);
            const lngMessage = getFieldErrorMessage(coordError?.lng);
            const isDragging = dragSrcIndex === coordIndex;
            const isDropTarget = dragOverIndex === coordIndex && !isDragging;

            return (
              <Grid
                container
                spacing={2}
                key={coordField.id}
                alignItems="center"
                draggable
                onDragStart={() => setDragSrcIndex(coordIndex)}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (dragSrcIndex !== null && dragSrcIndex !== coordIndex) {
                    setDragOverIndex(coordIndex);
                  }
                }}
                onDrop={() => {
                  if (dragSrcIndex !== null && dragSrcIndex !== coordIndex) {
                    moveCoordinate(dragSrcIndex, coordIndex);
                  }
                  setDragSrcIndex(null);
                  setDragOverIndex(null);
                }}
                onDragEnd={() => {
                  setDragSrcIndex(null);
                  setDragOverIndex(null);
                }}
                sx={{
                  mb: 1,
                  opacity: isDragging ? 0.4 : 1,
                  outline: isDropTarget
                    ? (theme) => `2px solid ${theme.palette.primary.main}`
                    : "none",
                  borderRadius: 1,
                }}
              >
                <Grid
                  item
                  xs="auto"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mt: "16px",
                  }}
                >
                  <DragIndicatorIcon
                    fontSize="small"
                    sx={{ cursor: "grab", color: "text.disabled" }}
                  />
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      bgcolor: watchedAreaColor || "primary.main",
                      border: "2px solid white",
                      boxShadow: "0 0 4px rgba(0,0,0,0.4)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "9px",
                      fontWeight: 700,
                      color: "white",
                      flexShrink: 0,
                    }}
                  >
                    {coordIndex + 1}
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Latitude"
                    InputLabelProps={{ shrink: true }}
                    error={!!latMessage}
                    helperText={latMessage}
                    inputProps={{ step: "any" }}
                    defaultValue={
                      typeof coordField.lat === "number" ? coordField.lat : ""
                    }
                    onFocus={focusThisArea}
                    {...register(
                      `areas.${index}.coordinates.${coordIndex}.lat` as const,
                      {
                        setValueAs: (value) =>
                          value === "" || value === null
                            ? undefined
                            : parseFloat(value),
                      },
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Longitude"
                    InputLabelProps={{ shrink: true }}
                    error={!!lngMessage}
                    helperText={lngMessage}
                    inputProps={{ step: "any" }}
                    defaultValue={
                      typeof coordField.lng === "number" ? coordField.lng : ""
                    }
                    onFocus={focusThisArea}
                    {...register(
                      `areas.${index}.coordinates.${coordIndex}.lng` as const,
                      {
                        setValueAs: (value) =>
                          value === "" || value === null
                            ? undefined
                            : parseFloat(value),
                      },
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3} lg={2}>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <IconButton
                      aria-label="Populate with current location"
                      onClick={() => handleUseCurrentLocation(coordIndex)}
                      disabled={isFetchingLocation}
                    >
                      <MyLocationIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      aria-label="Remove coordinate"
                      disabled={coordinateFields.length <= 1}
                      onClick={() => removeCoordinate(coordIndex)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
              </Grid>
            );
          })}

          <Button
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => appendCoordinate({ lat: undefined, lng: undefined })}
          >
            Add coordinate
          </Button>
        </AccordionDetails>
      </Accordion>
    );
  },
);

const PointEditor = memo(
  ({
  index,
  poiKey,
  register,
  errors,
  onRemove,
  field,
  control,
  setValue,
  onZoomToPoint,
  onOpenPointMap,
  expanded,
  onToggleExpand,
}: {
  index: number;
  poiKey: string;
  register: UseFormRegister<InteractiveMapFormValues>;
  errors: FieldErrors<InteractiveMapFormValues>;
  onRemove: (index: number) => void;
  field: FieldArrayWithId<InteractiveMapFormValues, "pointsOfInterest", "id">;
  control: Control<InteractiveMapFormValues>;
  setValue: UseFormSetValue<InteractiveMapFormValues>;
  onZoomToPoint: (index: number) => void;
  onOpenPointMap?: (poiKey: string) => void;
  expanded: boolean;
  onToggleExpand: (poiKey: string, open: boolean) => void;
}) => {
  const isSuperAdmin = useIsSuperAdmin();
  const poiErrors = errors.pointsOfInterest?.[index];
  const nameError = getFieldErrorMessage(poiErrors?.name);
  const categoryError = getFieldErrorMessage(poiErrors?.category);
  const latMessage = getFieldErrorMessage(poiErrors?.lat);
  const lngMessage = getFieldErrorMessage(poiErrors?.lng);
  const vendorError = getFieldErrorMessage(poiErrors?.vendorId);
  const stageError = getFieldErrorMessage(poiErrors?.stageId);
  const iconNameError = getFieldErrorMessage(poiErrors?.iconName);

  const categoryValue = useWatch({
    control,
    name: `pointsOfInterest.${index}.category` as const,
  });
  const stageIdValue = useWatch({
    control,
    name: `pointsOfInterest.${index}.stageId` as const,
  });
  const vendorIdValue = useWatch({
    control,
    name: `pointsOfInterest.${index}.vendorId` as const,
  });
  const latValue = useWatch({
    control,
    name: `pointsOfInterest.${index}.lat` as const,
  });
  const lngValue = useWatch({
    control,
    name: `pointsOfInterest.${index}.lng` as const,
  });
  const isHidden = useWatch({
    control,
    name: `pointsOfInterest.${index}.hidden` as const,
  });
  const iconNameValue = useWatch({
    control,
    name: `pointsOfInterest.${index}.iconName` as const,
  });

  const isStageCategory = categoryValue === "stage";
  const isVendorCategory = vendorCategories.includes(categoryValue ?? "");
  const canZoomToPoint =
    typeof latValue === "number" && typeof lngValue === "number";
  const descriptionMd = isStageCategory || isVendorCategory ? 12 : 5;
  const pickerMd = 6;

  useEffect(() => {
    if (isStageCategory) {
      if (vendorIdValue) {
        setValue(`pointsOfInterest.${index}.vendorId`, undefined, {
          shouldDirty: true,
          shouldTouch: true,
        });
      }
      return;
    }

    if (isVendorCategory) {
      if (stageIdValue) {
        setValue(`pointsOfInterest.${index}.stageId`, undefined, {
          shouldDirty: true,
          shouldTouch: true,
        });
      }
      return;
    }

    if (stageIdValue) {
      setValue(`pointsOfInterest.${index}.stageId`, undefined, {
        shouldDirty: true,
        shouldTouch: true,
      });
    }
    if (vendorIdValue) {
      setValue(`pointsOfInterest.${index}.vendorId`, undefined, {
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  }, [
    index,
    isStageCategory,
    isVendorCategory,
    setValue,
    stageIdValue,
    vendorIdValue,
  ]);

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, open) => onToggleExpand(poiKey, open)}
      sx={{ mb: 2 }}
      disableGutters
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            gap: 2,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {field.name || `Point ${index + 1}`}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconButton
              aria-label="Zoom to point on map"
              onClick={(event) => {
                event.stopPropagation();
                onZoomToPoint(index);
              }}
              disabled={!canZoomToPoint}
            >
              <ZoomInIcon fontSize="small" />
            </IconButton>
            <IconButton
              aria-label="Edit point on expanded map"
              onClick={(event) => {
                event.stopPropagation();
                onOpenPointMap?.(poiKey);
              }}
              disabled={!canZoomToPoint}
            >
              <OpenInFullIcon fontSize="small" />
            </IconButton>
            <IconButton
              aria-label={isHidden ? "Show on app" : "Hide from app"}
              onClick={(event) => {
                event.stopPropagation();
                setValue(`pointsOfInterest.${index}.hidden`, !isHidden, {
                  shouldDirty: true,
                });
              }}
              color={isHidden ? "warning" : "default"}
            >
              {isHidden ? (
                <VisibilityOffIcon fontSize="small" />
              ) : (
                <VisibilityIcon fontSize="small" />
              )}
            </IconButton>
            <IconButton
              aria-label="Remove point"
              onClick={(event) => {
                event.stopPropagation();
                const pointLabel = field.name || `Point ${index + 1}`;
                confirmAlert({
                  title: "Delete this point?",
                  message: `Delete ${pointLabel}? Are you sure? this can't be undone`,
                  buttons: [
                    {
                      label: "Yes",
                      onClick: () => onRemove(index),
                    },
                    {
                      label: "No",
                      onClick: () => {},
                    },
                  ],
                });
              }}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Name"
              InputLabelProps={{ shrink: true }}
              error={!!nameError}
              helperText={nameError}
              defaultValue={field.name}
              {...register(`pointsOfInterest.${index}.name` as const)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Category"
              InputLabelProps={{ shrink: true }}
              error={!!categoryError}
              helperText={categoryError}
              defaultValue={field.category ?? pointCategories[0].value}
              {...register(`pointsOfInterest.${index}.category` as const)}
            >
              {pointCategories.map((category) => (
                <MenuItem key={category.value} value={category.value}>
                  {category.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={descriptionMd}>
            <TextField
              fullWidth
              label="Description"
              InputLabelProps={{ shrink: true }}
              defaultValue={field.description ?? ""}
              {...register(`pointsOfInterest.${index}.description` as const)}
            />
          </Grid>
          {isStageCategory && (
            <Grid item xs={12} md={pickerMd}>
              <Controller
                control={control}
                name={`pointsOfInterest.${index}.stageId` as const}
                render={({ field: controllerField }) => (
                  <StagePicker
                    festivalId=""
                    value={controllerField.value ?? ""}
                    handleChange={(value) => controllerField.onChange(value)}
                  />
                )}
              />
              {stageError && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ display: "block", mt: 0.5 }}
                >
                  {stageError}
                </Typography>
              )}
            </Grid>
          )}
          {isVendorCategory && (
            <Grid item xs={12} md={pickerMd}>
              <Controller
                control={control}
                name={`pointsOfInterest.${index}.vendorId` as const}
                render={({ field: controllerField }) => (
                  <VendorPicker
                    value={controllerField.value ?? undefined}
                    controlStyle={{ marginTop: 0, marginBottom: 0 }}
                    handleChange={(value) => controllerField.onChange(value)}
                  />
                )}
              />
              {vendorError && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ display: "block", mt: 0.5 }}
                >
                  {vendorError}
                </Typography>
              )}
            </Grid>
          )}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="number"
              label="Latitude"
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: "any" }}
              error={!!latMessage}
              helperText={latMessage}
              defaultValue={typeof field.lat === "number" ? field.lat : ""}
              {...register(`pointsOfInterest.${index}.lat` as const, {
                setValueAs: (value) =>
                  value === "" || value === null
                    ? undefined
                    : parseFloat(value),
              })}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="number"
              label="Longitude"
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: "any" }}
              error={!!lngMessage}
              helperText={lngMessage}
              defaultValue={typeof field.lng === "number" ? field.lng : ""}
              {...register(`pointsOfInterest.${index}.lng` as const, {
                setValueAs: (value) =>
                  value === "" || value === null
                    ? undefined
                    : parseFloat(value),
              })}
            />
          </Grid>
          <Grid item xs={iconNameValue ? 8 : 12}>
            <TextField
              fullWidth
              label="Icon filename"
              InputLabelProps={{ shrink: true }}
              error={!!iconNameError}
              helperText={iconNameError}
              defaultValue={field.iconName ?? ""}
              {...register(`pointsOfInterest.${index}.iconName` as const)}
            />
          </Grid>
          {iconNameValue && (
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Icon scale"
                type="number"
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: "0.1", min: "0.1" }}
                defaultValue={field.iconScale ?? ""}
                {...register(`pointsOfInterest.${index}.iconScale` as const, {
                  setValueAs: (v) =>
                    v === "" || v === null ? null : parseFloat(v),
                })}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <Controller
              control={control}
              name={`pointsOfInterest.${index}.tags` as const}
              render={({ field: tagsField }) => (
                <TagsInput
                  value={tagsField.value ?? []}
                  onChange={tagsField.onChange}
                />
              )}
            />
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
  },
);

interface MapPreviewProps {
  areas?: MapAreaFormValue[];
  points?: MapPointFormValue[];
  lines?: MapLineFormValue[];
  activeTab: "areas" | "points" | "lines";
  onTabChange: (tab: "areas" | "points" | "lines") => void;
  pointCaptureEnabled: boolean;
  onTogglePointCapture: () => void;
  onAddPointFromMap: (coords: { lat: number; lng: number }) => void;
  areaCaptureEnabled: boolean;
  onToggleAreaCapture: () => void;
  onAddAreaPointFromMap: (coords: { lat: number; lng: number }) => void;
  selectedAreaId: string | null;
  onSelectArea: (id: string | null) => void;
  onUndoLastAreaPoint: () => void;
  canUndoAreaPoint: boolean;
  lineCaptureEnabled: boolean;
  onToggleLineCapture: () => void;
  onAddLinePointFromMap: (coords: { lat: number; lng: number }) => void;
  selectedLineId: string | null;
  onSelectLine: (id: string | null) => void;
  onUndoLastLinePoint: () => void;
  canUndoLinePoint: boolean;
  onMoveLineCoordinate?: (params: {
    lineId: string;
    coordIndex: number;
    lat: number;
    lng: number;
  }) => void;
  pointMoveTargetId: string | null;
  onSelectPointForMove: (pointId: string) => void;
  onMovePointFromMap: (coords: { lat: number; lng: number }) => void;
  onCancelPointMove: () => void;
  focusTarget: MapFocusTarget | null;
  expandedMapOpen?: boolean;
  onExpandedMapClose?: () => void;
  onMoveAreaCoordinate?: (params: {
    areaId: string;
    coordIndex: number;
    lat: number;
    lng: number;
  }) => void;
  onMovePoi?: (params: { pointId: string; lat: number; lng: number }) => void;
  visibleCategories: Set<string>;
  onCategoryChipClick: (value: string) => void;
  onResetCategories: () => void;
}

interface NormalizedAreaData {
  id: string;
  name?: string;
  color: string;
  coordinates: { lat: number; lng: number }[];
}

// Vertex marker icons only vary by color+label, so they're cached at module
// scope instead of rebuilt (new DOM string + object) on every render.
const vertexIconCache = new Map<string, L.DivIcon>();
function getVertexIcon(color: string, label: number): L.DivIcon {
  const key = `${color}|${label}`;
  let icon = vertexIconCache.get(key);
  if (!icon) {
    icon = L.divIcon({
      className: "",
      html: `<div style="width:20px;height:20px;border-radius:50%;background:${color};border:2px solid #ffffff;box-shadow:0 0 4px rgba(0,0,0,0.5);display:flex;align-items:center;justify-content:center;font-size:9px;color:white;font-weight:700;line-height:1;cursor:grab;">${label}</div>`,
      iconSize: [20, 20],
      iconAnchor: [10, 10],
    });
    vertexIconCache.set(key, icon);
  }
  return icon;
}

const VertexMarker = memo(
  ({
    position,
    color,
    label,
    onDragEnd,
  }: {
    position: LatLngExpression;
    color: string;
    label: number;
    onDragEnd: (lat: number, lng: number) => void;
  }) => {
    const icon = useMemo(() => getVertexIcon(color, label), [color, label]);
    const eventHandlers = useMemo(
      () => ({
        dragend(e: L.LeafletEvent) {
          const { lat, lng } = (e.target as L.Marker).getLatLng();
          onDragEnd(lat, lng);
        },
      }),
      [onDragEnd],
    );
    return (
      <Marker
        position={position}
        draggable
        icon={icon}
        eventHandlers={eventHandlers}
      />
    );
  },
);

// Renders one area's polygon/polyline (+ its vertex markers when selected).
// Memoized so editing one area's fields doesn't re-render every other area
// or force Leaflet to rebind listeners on the whole overlay set.
const AreaOverlay = memo(
  ({
    area,
    isSelected,
    captureActive,
    onCapture,
    onMoveAreaCoordinate,
    keySuffix,
  }: {
    area: NormalizedAreaData;
    isSelected: boolean;
    captureActive: boolean;
    onCapture: (lat: number, lng: number) => void;
    onMoveAreaCoordinate?: (params: {
      areaId: string;
      coordIndex: number;
      lat: number;
      lng: number;
    }) => void;
    keySuffix: string;
  }) => {
    if (area.coordinates.length === 0) return null;
    const positions = area.coordinates.map(
      (coord) => [coord.lat, coord.lng] as LatLngExpression,
    );
    const eventHandlers = captureActive
      ? {
          click: (event: L.LeafletMouseEvent) =>
            onCapture(event.latlng.lat, event.latlng.lng),
        }
      : undefined;

    const vertexMarkers = isSelected
      ? area.coordinates.map((coord, coordIndex) => (
          <VertexMarker
            key={`${area.id}-vertex-${coordIndex}-${keySuffix}`}
            position={[coord.lat, coord.lng]}
            color={area.color}
            label={coordIndex + 1}
            onDragEnd={(lat, lng) =>
              onMoveAreaCoordinate?.({ areaId: area.id, coordIndex, lat, lng })
            }
          />
        ))
      : null;

    if (positions.length < 3) {
      return (
        <>
          <Polyline
            key={`${area.id}-polyline-${keySuffix}`}
            positions={positions}
            pathOptions={{ color: area.color, weight: isSelected ? 4 : 2 }}
            interactive={!captureActive}
            eventHandlers={eventHandlers}
          />
          {vertexMarkers}
        </>
      );
    }

    return (
      <>
        <Polygon
          key={`${area.id}-${keySuffix}`}
          positions={positions}
          pathOptions={{
            color: area.color,
            fillColor: area.color,
            fillOpacity: isSelected ? 0.35 : 0.2,
            weight: isSelected ? 4 : 2,
          }}
          interactive={!captureActive}
          eventHandlers={eventHandlers}
        >
          <Popup>
            <div>
              <strong>{area.name || "Unnamed area"}</strong>
              <div>{area.coordinates.length} points</div>
            </div>
          </Popup>
        </Polygon>
        {vertexMarkers}
      </>
    );
  },
);

// Same idea as AreaOverlay, for lines.
const LineOverlay = memo(
  ({
    line,
    isSelected,
    captureActive,
    onCapture,
    onMoveLineCoordinate,
    keySuffix,
  }: {
    line: NormalizedAreaData;
    isSelected: boolean;
    captureActive: boolean;
    onCapture: (lat: number, lng: number) => void;
    onMoveLineCoordinate?: (params: {
      lineId: string;
      coordIndex: number;
      lat: number;
      lng: number;
    }) => void;
    keySuffix: string;
  }) => {
    if (line.coordinates.length === 0) return null;
    const positions = line.coordinates.map(
      (coord) => [coord.lat, coord.lng] as LatLngExpression,
    );
    const eventHandlers = captureActive
      ? {
          click: (event: L.LeafletMouseEvent) =>
            onCapture(event.latlng.lat, event.latlng.lng),
        }
      : undefined;

    const vertexMarkers = isSelected
      ? line.coordinates.map((coord, coordIndex) => (
          <VertexMarker
            key={`${line.id}-vertex-${coordIndex}-${keySuffix}`}
            position={[coord.lat, coord.lng]}
            color={line.color}
            label={coordIndex + 1}
            onDragEnd={(lat, lng) =>
              onMoveLineCoordinate?.({ lineId: line.id, coordIndex, lat, lng })
            }
          />
        ))
      : null;

    return (
      <>
        <Polyline
          key={`${line.id}-${keySuffix}`}
          positions={positions}
          pathOptions={{
            color: line.color,
            weight: isSelected ? 5 : 3,
            opacity: isSelected ? 1 : 0.7,
          }}
          interactive={!captureActive}
          eventHandlers={eventHandlers}
        >
          <Popup>
            <div>
              <strong>{line.name || "Unnamed line"}</strong>
              <div>{line.coordinates.length} points</div>
            </div>
          </Popup>
        </Polyline>
        {vertexMarkers}
      </>
    );
  },
);

// One POI marker + tooltip + popup. Memoized so a large POI list only
// re-renders the pin whose own data actually changed.
const PointMarker = memo(
  ({
    point,
    isMoveTarget,
    captureActive,
    onCapture,
    onSelectForMove,
    onMove,
  }: {
    point: MapPointFormValue & { lat: number; lng: number };
    isMoveTarget: boolean;
    captureActive: boolean;
    onCapture: (lat: number, lng: number) => void;
    onSelectForMove: (pointId: string) => void;
    onMove: (pointId: string, lat: number, lng: number) => void;
  }) => {
    const eventHandlers = captureActive
      ? {
          click: (event: L.LeafletMouseEvent) =>
            onCapture(event.latlng.lat, event.latlng.lng),
        }
      : {
          click: () => onSelectForMove(point.id),
          dragend: (e: L.LeafletEvent) => {
            const { lat, lng } = (e.target as L.Marker).getLatLng();
            onMove(point.id, lat, lng);
          },
        };
    return (
      <Marker
        position={[point.lat, point.lng]}
        draggable={!captureActive}
        interactive={!captureActive}
        eventHandlers={eventHandlers}
      >
        <Tooltip permanent direction="top" offset={[0, -10]}>
          {isMoveTarget
            ? `${point.name || point.category || "Point"} (editing)`
            : point.name || point.category || "Point"}
        </Tooltip>
        <Popup>
          <div>
            <strong>{point.name}</strong>
            <div>{point.category}</div>
            {point.description && <div>{point.description}</div>}
          </div>
        </Popup>
      </Marker>
    );
  },
);

const MapPreview = memo(({
  areas,
  points,
  lines,
  activeTab,
  onTabChange,
  pointCaptureEnabled,
  onTogglePointCapture,
  onAddPointFromMap,
  areaCaptureEnabled,
  onToggleAreaCapture,
  onAddAreaPointFromMap,
  selectedAreaId,
  onSelectArea,
  onUndoLastAreaPoint,
  canUndoAreaPoint,
  lineCaptureEnabled,
  onToggleLineCapture,
  onAddLinePointFromMap,
  selectedLineId,
  onSelectLine,
  onUndoLastLinePoint,
  canUndoLinePoint,
  onMoveLineCoordinate,
  pointMoveTargetId,
  onSelectPointForMove,
  onMovePointFromMap,
  onCancelPointMove,
  focusTarget,
  expandedMapOpen = false,
  onExpandedMapClose,
  onMoveAreaCoordinate,
  onMovePoi,
  visibleCategories,
  onCategoryChipClick,
  onResetCategories,
}: MapPreviewProps) => {
  const normalizedAreas = useMemo(() => {
    return (areas ?? []).map((area) => {
      const coords =
        area.coordinates?.filter(
          (coord): coord is { lat: number; lng: number } =>
            typeof coord.lat === "number" && typeof coord.lng === "number",
        ) ?? [];

      return {
        id: area.id,
        name: area.name,
        color: area.color,
        coordinates: coords,
      };
    });
  }, [areas]);

  const selectedAreaData = useMemo(
    () => normalizedAreas.find((area) => area.id === selectedAreaId),
    [normalizedAreas, selectedAreaId],
  );

  const normalizedPoints = useMemo(() => {
    return (points ?? []).filter(
      (point): point is MapPointFormValue & { lat: number; lng: number } =>
        typeof point.lat === "number" && typeof point.lng === "number",
    );
  }, [points]);

  const visibleNormalizedPoints = useMemo(
    () =>
      normalizedPoints.filter((p) => visibleCategories.has(p.category ?? "")),
    [normalizedPoints, visibleCategories],
  );

  const normalizedLines = useMemo(() => {
    return (lines ?? []).map((line) => {
      const coords =
        line.coordinates?.filter(
          (coord): coord is { lat: number; lng: number } =>
            typeof coord.lat === "number" && typeof coord.lng === "number",
        ) ?? [];
      return {
        id: line.id,
        name: line.name,
        color: line.color,
        coordinates: coords,
      };
    });
  }, [lines]);

  const selectedLineData = useMemo(
    () => normalizedLines.find((line) => line.id === selectedLineId),
    [normalizedLines, selectedLineId],
  );

  const pointMoveTarget = useMemo(
    () => normalizedPoints.find((point) => point.id === pointMoveTargetId),
    [normalizedPoints, pointMoveTargetId],
  );
  const pointMoveEnabled = Boolean(pointMoveTargetId);

  const allCoords = useMemo(() => {
    return [
      ...normalizedAreas.flatMap((area) => area.coordinates),
      ...normalizedPoints.map((point) => ({ lat: point.lat, lng: point.lng })),
      ...normalizedLines.flatMap((line) => line.coordinates),
    ];
  }, [normalizedAreas, normalizedPoints, normalizedLines]);

  const hasCoords = allCoords.length > 0;
  const fallbackCenter: LatLngExpression = [52.5, -1.9];

  useEffect(() => {
    if (areaCaptureEnabled) {
      if (activeTab !== "areas") onTabChange("areas");
      return;
    }
    if (lineCaptureEnabled) {
      if (activeTab !== "lines") onTabChange("lines");
      return;
    }
    if (pointCaptureEnabled) {
      if (activeTab !== "points") onTabChange("points");
      return;
    }
    if (pointMoveEnabled && activeTab !== "points") {
      onTabChange("points");
    }
  }, [
    areaCaptureEnabled,
    lineCaptureEnabled,
    pointCaptureEnabled,
    pointMoveEnabled,
    activeTab,
    onTabChange,
  ]);

  const bounds = hasCoords
    ? allCoords.reduce(
        (acc, coord) => ({
          minLat: Math.min(acc.minLat, coord.lat),
          maxLat: Math.max(acc.maxLat, coord.lat),
          minLng: Math.min(acc.minLng, coord.lng),
          maxLng: Math.max(acc.maxLng, coord.lng),
        }),
        {
          minLat: Number.POSITIVE_INFINITY,
          maxLat: Number.NEGATIVE_INFINITY,
          minLng: Number.POSITIVE_INFINITY,
          maxLng: Number.NEGATIVE_INFINITY,
        },
      )
    : {
        minLat: fallbackCenter[0],
        maxLat: fallbackCenter[0],
        minLng: fallbackCenter[1],
        maxLng: fallbackCenter[1],
      };

  const latSpan = bounds.maxLat - bounds.minLat || 0.001;
  const lngSpan = bounds.maxLng - bounds.minLng || 0.001;

  const handleTabChange = (
    _: SyntheticEvent,
    value: "points" | "areas" | "lines",
  ) => {
    onTabChange(value);
    if (value !== "areas" && areaCaptureEnabled) onToggleAreaCapture();
    if (value !== "lines" && lineCaptureEnabled) onToggleLineCapture();
    if (value !== "points" && pointCaptureEnabled) onTogglePointCapture();
    if (value !== "points" && pointMoveEnabled) onCancelPointMove();
  };

  const handlePointCaptureClick = () => {
    if (activeTab !== "points") onTabChange("points");
    if (pointMoveEnabled) onCancelPointMove();
    if (lineCaptureEnabled) onToggleLineCapture();
    onTogglePointCapture();
  };

  const handleAreaCaptureClick = () => {
    if (activeTab !== "areas") onTabChange("areas");
    if (pointMoveEnabled) onCancelPointMove();
    if (lineCaptureEnabled) onToggleLineCapture();
    onToggleAreaCapture();
  };

  const handleLineCaptureClick = () => {
    if (activeTab !== "lines") onTabChange("lines");
    if (pointMoveEnabled) onCancelPointMove();
    if (areaCaptureEnabled) onToggleAreaCapture();
    onToggleLineCapture();
  };

  const paddedBounds: LatLngBoundsExpression = [
    [bounds.minLat - latSpan * 0.05, bounds.minLng - lngSpan * 0.05],
    [bounds.maxLat + latSpan * 0.05, bounds.maxLng + lngSpan * 0.05],
  ];

  const mapKey = hasCoords
    ? [
        ...normalizedAreas.map((a) => a.id),
        ...normalizedPoints.map((p) => p.id),
        ...normalizedLines.map((l) => l.id),
      ].join("|")
    : `${fallbackCenter[0]}-${fallbackCenter[1]}`;

  const captureActive =
    pointCaptureEnabled ||
    areaCaptureEnabled ||
    lineCaptureEnabled ||
    pointMoveEnabled;
  const handleOverlayCapture = useCallback(
    (lat: number, lng: number) => {
      if (pointMoveEnabled) {
        onMovePointFromMap({ lat, lng });
      } else if (areaCaptureEnabled) {
        onAddAreaPointFromMap({ lat, lng });
      } else if (lineCaptureEnabled) {
        onAddLinePointFromMap({ lat, lng });
      } else if (pointCaptureEnabled) {
        onAddPointFromMap({ lat, lng });
      }
    },
    [
      pointMoveEnabled,
      areaCaptureEnabled,
      lineCaptureEnabled,
      pointCaptureEnabled,
      onMovePointFromMap,
      onAddAreaPointFromMap,
      onAddLinePointFromMap,
      onAddPointFromMap,
    ],
  );
  const handlePointMoveDrag = useCallback(
    (pointId: string, lat: number, lng: number) => {
      onMovePoi?.({ pointId, lat, lng });
    },
    [onMovePoi],
  );
  const [internalMapDialogOpen, setInternalMapDialogOpen] = useState(false);
  const mapDialogOpen = expandedMapOpen || internalMapDialogOpen;

  const [dialogAreaFocusBounds, setDialogAreaFocusBounds] =
    useState<LatLngBoundsExpression | null>(null);
  useEffect(() => {
    if (!mapDialogOpen || !selectedAreaId) {
      setDialogAreaFocusBounds(null);
      return;
    }
    const area = normalizedAreas.find((a) => a.id === selectedAreaId);
    if (!area || area.coordinates.length < 2) {
      setDialogAreaFocusBounds(null);
      return;
    }
    setDialogAreaFocusBounds(
      area.coordinates.map((c) => [c.lat, c.lng] as [number, number]),
    );
    // normalizedAreas intentionally omitted — bounds are captured once on open, not on every coord change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapDialogOpen, selectedAreaId]);

  const [dialogLineFocusBounds, setDialogLineFocusBounds] =
    useState<LatLngBoundsExpression | null>(null);
  useEffect(() => {
    if (!mapDialogOpen || !selectedLineId) {
      setDialogLineFocusBounds(null);
      return;
    }
    const line = normalizedLines.find((l) => l.id === selectedLineId);
    if (!line || line.coordinates.length < 2) {
      setDialogLineFocusBounds(null);
      return;
    }
    setDialogLineFocusBounds(
      line.coordinates.map((c) => [c.lat, c.lng] as [number, number]),
    );
    // normalizedLines intentionally omitted — bounds are captured once on open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapDialogOpen, selectedLineId]);

  const handleCloseMapDialog = () => {
    setInternalMapDialogOpen(false);
    onExpandedMapClose?.();
  };
  const [satelliteView, setSatelliteView] = useState(false);

  const renderMap = ({
    height,
    keySuffix,
    showControls,
    focusBounds,
  }: {
    height: number;
    keySuffix: string;
    showControls?: boolean;
    focusBounds?: LatLngBoundsExpression | null;
  }) => (
    <Box
      sx={{
        borderRadius: 2,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        overflow: "hidden",
        ...(areaCaptureEnabled && {
          "& .leaflet-grab": { cursor: "crosshair" },
          "& .leaflet-container": { cursor: "crosshair" },
        }),
      }}
    >
      <MapContainer
        minZoom={0}
        maxZoom={35}
        key={`${mapKey}-${keySuffix}`}
        style={{ height, width: "100%" }}
        scrollWheelZoom
        {...(hasCoords
          ? { bounds: paddedBounds }
          : { center: fallbackCenter, zoom: 14 })}
      >
        {satelliteView ? (
          <TileLayer
            maxZoom={35}
            attribution="Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          />
        ) : (
          <TileLayer
            maxZoom={35}
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
        )}
        <MapClickHandler
          pointCaptureEnabled={pointCaptureEnabled}
          areaCaptureEnabled={areaCaptureEnabled}
          lineCaptureEnabled={lineCaptureEnabled}
          pointMoveEnabled={pointMoveEnabled}
          onPointCapture={onAddPointFromMap}
          onAreaCapture={onAddAreaPointFromMap}
          onLineCapture={onAddLinePointFromMap}
          onPointMove={onMovePointFromMap}
        />
        <MapFocusHandler focusTarget={focusTarget} focusBounds={focusBounds} />
        {normalizedAreas.map((area) => (
          <AreaOverlay
            key={`${area.id}-${keySuffix}`}
            area={area}
            isSelected={area.id === selectedAreaId}
            captureActive={captureActive}
            onCapture={handleOverlayCapture}
            onMoveAreaCoordinate={onMoveAreaCoordinate}
            keySuffix={keySuffix}
          />
        ))}
        {normalizedLines.map((line) => (
          <LineOverlay
            key={`${line.id}-${keySuffix}`}
            line={line}
            isSelected={line.id === selectedLineId}
            captureActive={captureActive}
            onCapture={handleOverlayCapture}
            onMoveLineCoordinate={onMoveLineCoordinate}
            keySuffix={keySuffix}
          />
        ))}
        {visibleNormalizedPoints.map((point) => (
          <PointMarker
            key={`${point.id}-${keySuffix}`}
            point={point}
            isMoveTarget={point.id === pointMoveTargetId}
            captureActive={captureActive}
            onCapture={handleOverlayCapture}
            onSelectForMove={onSelectPointForMove}
            onMove={handlePointMoveDrag}
          />
        ))}
      </MapContainer>
      {showControls && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 1,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {selectedAreaData
              ? `Drawing vertices for ${
                  selectedAreaData.name || "selected area"
                }`
              : "No area selected"}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            disabled={!canUndoAreaPoint}
            onClick={onUndoLastAreaPoint}
          >
            Undo last coordinate
          </Button>
        </Box>
      )}
    </Box>
  );

  return (
    <Paper elevation={0} sx={{ p: 3, height: "100%" }}>
      <Typography variant="h6" gutterBottom>
        Live preview
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Preview polygons and pins directly on OpenStreetMap tiles to verify
        locations. Use the button below to drop new points directly on the map.
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
        <Button
          size="small"
          variant={satelliteView ? "contained" : "outlined"}
          color="primary"
          onClick={() => setSatelliteView((prev: boolean) => !prev)}
        >
          {satelliteView ? "Street View" : "Satellite View"}
        </Button>
      </Box>
      {renderMap({ height: 420, keySuffix: "inline" })}
      {hasCoords ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, mb: 2, display: "block" }}
        >
          Bounding box lat ({bounds.minLat.toFixed(3)} –{" "}
          {bounds.maxLat.toFixed(3)}), lng ({bounds.minLng.toFixed(3)} –{" "}
          {bounds.maxLng.toFixed(3)})
        </Typography>
      ) : (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, mb: 2, display: "block" }}
        >
          No coordinates yet. Enable capture and click the map to drop your
          first point.
        </Typography>
      )}
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{ mb: 2 }}
        variant="fullWidth"
      >
        <Tab label="Points" value="points" />
        <Tab
          label="Areas"
          value="areas"
          disabled={normalizedAreas.length === 0}
        />
        <Tab
          label="Lines"
          value="lines"
          disabled={normalizedLines.length === 0}
        />
      </Tabs>
      {activeTab === "points" && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.5,
            mb: 2,
          }}
        >
          <Button
            size="small"
            variant={pointCaptureEnabled ? "contained" : "outlined"}
            color={pointCaptureEnabled ? "success" : "primary"}
            onClick={handlePointCaptureClick}
          >
            {pointCaptureEnabled
              ? "Click map to add point"
              : "Drop point on map"}
          </Button>
          <Typography variant="caption" color="text.secondary">
            {pointMoveEnabled
              ? `Editing ${pointMoveTarget?.name || "point"}: click map to set the new location.`
              : pointCaptureEnabled
                ? "Now click anywhere on the map to create a new point of interest."
                : "Enable capture and click the map to add a point. Click an existing marker to move it."}
          </Typography>
          {pointMoveEnabled && (
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              onClick={onCancelPointMove}
            >
              Cancel move
            </Button>
          )}
          {(() => {
            const presentCategories = pointCategories.filter((cat) =>
              normalizedPoints.some((p) => p.category === cat.value),
            );
            if (presentCategories.length < 2) return null;
            const allVisible = presentCategories.every((cat) =>
              visibleCategories.has(cat.value),
            );
            return (
              <Box sx={{ mt: 0.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mb: 0.5,
                  }}
                >
                  <FilterListIcon
                    fontSize="small"
                    color="action"
                    sx={{ fontSize: 14 }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    Filter by category
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {presentCategories.map((cat) => {
                    const on = visibleCategories.has(cat.value);
                    return (
                      <Chip
                        key={cat.value}
                        label={cat.label}
                        size="small"
                        variant={on ? "filled" : "outlined"}
                        color={on ? "primary" : "default"}
                        onClick={() => onCategoryChipClick(cat.value)}
                      />
                    );
                  })}
                  {!allVisible && (
                    <Chip
                      label="Show all"
                      size="small"
                      variant="outlined"
                      onClick={onResetCategories}
                    />
                  )}
                </Box>
              </Box>
            );
          })()}
        </Box>
      )}
      {activeTab === "areas" &&
        (normalizedAreas.length > 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              mb: 2,
            }}
          >
            <TextField
              select
              size="small"
              label="Area to add to"
              value={selectedAreaId ?? ""}
              onChange={(event) =>
                onSelectArea(event.target.value ? event.target.value : null)
              }
              InputLabelProps={{ shrink: true }}
            >
              {normalizedAreas.map((area, index) => (
                <MenuItem key={area.id} value={area.id}>
                  {area.name || `Area ${index + 1}`}
                </MenuItem>
              ))}
            </TextField>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Button
                size="small"
                variant={areaCaptureEnabled ? "contained" : "outlined"}
                color={areaCaptureEnabled ? "success" : "primary"}
                onClick={handleAreaCaptureClick}
                disabled={!selectedAreaId}
              >
                {areaCaptureEnabled
                  ? "Click map to add coordinate"
                  : "Draw area via map"}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                disabled={!canUndoAreaPoint}
                onClick={onUndoLastAreaPoint}
              >
                Undo last coordinate
              </Button>
              {areaCaptureEnabled && (
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={handleAreaCaptureClick}
                >
                  Finalize changes
                </Button>
              )}
              <Typography variant="caption" color="text.secondary">
                {areaCaptureEnabled
                  ? "Click the map to append vertices. Turn off when finished."
                  : "Select an area, then click to add coordinates."}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
            Create an area first to start drawing polygons.
          </Typography>
        ))}
      {activeTab === "lines" &&
        (normalizedLines.length > 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
            <TextField
              select
              size="small"
              label="Line to add to"
              value={selectedLineId ?? ""}
              onChange={(event) =>
                onSelectLine(event.target.value ? event.target.value : null)
              }
              InputLabelProps={{ shrink: true }}
            >
              {normalizedLines.map((line, index) => (
                <MenuItem key={line.id} value={line.id}>
                  {line.name || `Line ${index + 1}`}
                </MenuItem>
              ))}
            </TextField>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Button
                size="small"
                variant={lineCaptureEnabled ? "contained" : "outlined"}
                color={lineCaptureEnabled ? "success" : "primary"}
                onClick={handleLineCaptureClick}
                disabled={!selectedLineId}
              >
                {lineCaptureEnabled
                  ? "Click map to add coordinate"
                  : "Draw line via map"}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                disabled={!canUndoLinePoint}
                onClick={onUndoLastLinePoint}
              >
                Undo last coordinate
              </Button>
              <Typography variant="caption" color="text.secondary">
                {lineCaptureEnabled
                  ? "Click the map to append points. Turn off when finished."
                  : "Select a line, then click to add coordinates."}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
            Create a line first to start drawing.
          </Typography>
        ))}
      {(areaCaptureEnabled ||
        lineCaptureEnabled ||
        pointCaptureEnabled ||
        pointMoveEnabled) && (
        <Button
          size="small"
          variant="outlined"
          sx={{ mb: 2 }}
          onClick={() => setInternalMapDialogOpen(true)}
        >
          Open large map
        </Button>
      )}
      <Dialog
        fullWidth
        maxWidth="lg"
        open={mapDialogOpen}
        onClose={handleCloseMapDialog}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 1,
          }}
        >
          Expanded map
          <IconButton onClick={handleCloseMapDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              mb: 2,
              gap: 1,
            }}
          >
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {activeTab === "points"
                  ? pointMoveEnabled
                    ? `Moving: ${pointMoveTarget?.name || "point"}`
                    : "Points of interest"
                  : activeTab === "lines"
                    ? selectedLineData?.name || "Selected line"
                    : selectedAreaData?.name || "Selected area"}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                {activeTab === "points"
                  ? pointMoveEnabled
                    ? "Click the map to place the point at a new location"
                    : "Click the map to add a new point of interest"
                  : "Drag numbered markers to reposition points"}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              {activeTab === "points" ? (
                <>
                  <Button
                    size="small"
                    variant={pointCaptureEnabled ? "contained" : "outlined"}
                    color={pointCaptureEnabled ? "success" : "primary"}
                    onClick={onTogglePointCapture}
                  >
                    {pointCaptureEnabled
                      ? "Click map to add point"
                      : "Add point on map"}
                  </Button>
                  {pointMoveEnabled && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      onClick={onCancelPointMove}
                    >
                      Cancel move
                    </Button>
                  )}
                </>
              ) : activeTab === "lines" ? (
                <>
                  <Button
                    size="small"
                    variant={lineCaptureEnabled ? "contained" : "outlined"}
                    color={lineCaptureEnabled ? "success" : "primary"}
                    disabled={!selectedLineId}
                    onClick={onToggleLineCapture}
                  >
                    {lineCaptureEnabled
                      ? "Click map to add coordinate"
                      : "Draw on map"}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    disabled={!canUndoLinePoint}
                    onClick={onUndoLastLinePoint}
                  >
                    Undo
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    size="small"
                    variant={areaCaptureEnabled ? "contained" : "outlined"}
                    color={areaCaptureEnabled ? "success" : "primary"}
                    disabled={!selectedAreaId}
                    onClick={onToggleAreaCapture}
                  >
                    {areaCaptureEnabled
                      ? "Click map to add coordinate"
                      : "Draw on map"}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    disabled={!canUndoAreaPoint}
                    onClick={onUndoLastAreaPoint}
                  >
                    Undo
                  </Button>
                </>
              )}
            </Box>
          </Box>
          {activeTab === "points" &&
            (() => {
              const presentCategories = pointCategories.filter((cat) =>
                normalizedPoints.some((p) => p.category === cat.value),
              );
              if (presentCategories.length < 2) return null;
              const allVisible = presentCategories.every((cat) =>
                visibleCategories.has(cat.value),
              );
              return (
                <Box
                  sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 2 }}
                >
                  {presentCategories.map((cat) => {
                    const on = visibleCategories.has(cat.value);
                    return (
                      <Chip
                        key={cat.value}
                        label={cat.label}
                        size="small"
                        variant={on ? "filled" : "outlined"}
                        color={on ? "primary" : "default"}
                        onClick={() => onCategoryChipClick(cat.value)}
                      />
                    );
                  })}
                  {!allVisible && (
                    <Chip
                      label="Show all"
                      size="small"
                      variant="outlined"
                      onClick={onResetCategories}
                    />
                  )}
                </Box>
              );
            })()}
          {renderMap({
            height: 650,
            keySuffix: "dialog",
            showControls: false,
            focusBounds:
              activeTab === "lines"
                ? dialogLineFocusBounds
                : activeTab === "points"
                  ? null
                  : dialogAreaFocusBounds,
          })}
        </DialogContent>
      </Dialog>
    </Paper>
  );
});

interface MapClickHandlerProps {
  pointCaptureEnabled: boolean;
  areaCaptureEnabled: boolean;
  lineCaptureEnabled: boolean;
  pointMoveEnabled: boolean;
  onPointCapture: (coords: { lat: number; lng: number }) => void;
  onAreaCapture: (coords: { lat: number; lng: number }) => void;
  onLineCapture: (coords: { lat: number; lng: number }) => void;
  onPointMove: (coords: { lat: number; lng: number }) => void;
}

const MapFocusHandler = ({
  focusTarget,
  focusBounds,
}: {
  focusTarget: MapFocusTarget | null;
  focusBounds?: LatLngBoundsExpression | null;
}) => {
  const map = useMap();

  useEffect(() => {
    if (!focusTarget) return;
    const zoomLevel = Math.max(map.getZoom(), 17);
    map.flyTo([focusTarget.lat, focusTarget.lng], zoomLevel, {
      animate: true,
      duration: 0.8,
    });
  }, [map, focusTarget]);

  useEffect(() => {
    if (!focusBounds) return;
    map.fitBounds(focusBounds, {
      padding: [40, 40],
      animate: true,
      duration: 0.8,
    });
  }, [map, focusBounds]);

  return null;
};

const MapClickHandler = ({
  pointCaptureEnabled,
  areaCaptureEnabled,
  lineCaptureEnabled,
  pointMoveEnabled,
  onPointCapture,
  onAreaCapture,
  onLineCapture,
  onPointMove,
}: MapClickHandlerProps) => {
  useMapEvents({
    click(e) {
      if (pointMoveEnabled) {
        onPointMove({ lat: e.latlng.lat, lng: e.latlng.lng });
        return;
      }
      if (areaCaptureEnabled) {
        onAreaCapture({ lat: e.latlng.lat, lng: e.latlng.lng });
        return;
      }
      if (lineCaptureEnabled) {
        onLineCapture({ lat: e.latlng.lat, lng: e.latlng.lng });
        return;
      }
      if (pointCaptureEnabled) {
        onPointCapture({ lat: e.latlng.lat, lng: e.latlng.lng });
      }
    },
  });
  return null;
};

const LineEditor = memo(
  ({
  index,
  lineKey,
  control,
  register,
  errors,
  onRemove,
  field,
  onFocusLine,
  onRegisterLineHelpers,
  onUnregisterLineHelpers,
  setValue,
  expanded,
  onToggleExpand,
  onOpenLineMap,
}: {
  index: number;
  lineKey: string;
  control: Control<InteractiveMapFormValues>;
  register: UseFormRegister<InteractiveMapFormValues>;
  errors: FieldErrors<InteractiveMapFormValues>;
  onRemove: (index: number) => void;
  field: FieldArrayWithId<InteractiveMapFormValues, "lines", "id">;
  onFocusLine: (lineKey: string) => void;
  onRegisterLineHelpers?: (
    lineId: string,
    helpers: { appendCoordinate: (value: CoordinateFormValue) => void },
  ) => void;
  onUnregisterLineHelpers?: (lineId: string) => void;
  setValue: UseFormSetValue<InteractiveMapFormValues>;
  expanded: boolean;
  onToggleExpand: (lineKey: string, open: boolean) => void;
  onOpenLineMap?: (lineKey: string) => void;
}) => {
  const {
    fields: coordinateFields,
    append: appendCoordinate,
    remove: removeCoordinate,
    move: moveCoordinate,
  } = useFieldArray({ control, name: `lines.${index}.coordinates` });
  const [dragSrcIndex, setDragSrcIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [isFetchingLocation, setIsFetchingLocation] = useState(false);

  const lineErrors = (errors as any)?.lines?.[index];
  const nameError = getFieldErrorMessage(lineErrors?.name);
  const colorError = getFieldErrorMessage(lineErrors?.color);
  const watchedLineColor =
    useWatch({ control, name: `lines.${index}.color` as const }) ?? field.color;
  const isHidden = useWatch({
    control,
    name: `lines.${index}.hidden` as const,
  });

  const lineColorInputRef = useRef<HTMLInputElement | null>(null);
  const {
    ref: lineColorRegRef,
    onChange: _lineColorOnChange,
    ...lineColorRegProps
  } = register(`lines.${index}.color` as const);
  useEffect(() => {
    const input = lineColorInputRef.current;
    if (!input) return;
    const handler = (e: Event) => {
      setValue(`lines.${index}.color`, (e.target as HTMLInputElement).value, {
        shouldDirty: true,
        shouldValidate: false,
      });
    };
    input.addEventListener("change", handler);
    return () => input.removeEventListener("change", handler);
  }, [index, setValue]);

  useEffect(() => {
    onRegisterLineHelpers?.(field.id, { appendCoordinate });
    return () => {
      onUnregisterLineHelpers?.(field.id);
    };
  }, [
    appendCoordinate,
    field.id,
    onRegisterLineHelpers,
    onUnregisterLineHelpers,
  ]);

  const focusThisLine = () => onFocusLine(lineKey);

  const handleUseCurrentLocation = (coordIndex: number) => {
    if (!navigator?.geolocation) {
      toast("Geolocation is not supported in this browser");
      return;
    }
    setIsFetchingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setIsFetchingLocation(false);
        setValue(
          `lines.${index}.coordinates.${coordIndex}.lat`,
          position.coords.latitude,
          {
            shouldDirty: true,
            shouldTouch: true,
          },
        );
        setValue(
          `lines.${index}.coordinates.${coordIndex}.lng`,
          position.coords.longitude,
          {
            shouldDirty: true,
            shouldTouch: true,
          },
        );
      },
      () => {
        setIsFetchingLocation(false);
        toast("Unable to retrieve your location");
      },
      { enableHighAccuracy: true },
    );
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, open) => onToggleExpand(lineKey, open)}
      sx={{ mb: 2 }}
      disableGutters
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        onClick={focusThisLine}
        sx={{ px: 2 }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            gap: 2,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {field.name || `Line ${index + 1}`}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {coordinateFields.length > 0 && (
              <IconButton
                aria-label="Edit line on expanded map"
                onClick={(event) => {
                  event.stopPropagation();
                  onOpenLineMap?.(lineKey);
                }}
              >
                <OpenInFullIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton
              aria-label={isHidden ? "Show on app" : "Hide from app"}
              onClick={(event) => {
                event.stopPropagation();
                setValue(`lines.${index}.hidden`, !isHidden, {
                  shouldDirty: true,
                });
              }}
              color={isHidden ? "warning" : "default"}
            >
              {isHidden ? (
                <VisibilityOffIcon fontSize="small" />
              ) : (
                <VisibilityIcon fontSize="small" />
              )}
            </IconButton>
            <IconButton
              aria-label="Remove line"
              onClick={(event) => {
                event.stopPropagation();
                onRemove(index);
              }}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Name"
              InputLabelProps={{ shrink: true }}
              error={!!nameError}
              helperText={nameError}
              defaultValue={field.name}
              onFocus={focusThisLine}
              {...register(`lines.${index}.name` as const)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="color"
              label="Color"
              InputLabelProps={{ shrink: true }}
              error={!!colorError}
              helperText={colorError}
              defaultValue={field.color}
              onFocus={focusThisLine}
              {...lineColorRegProps}
              inputRef={(el) => {
                lineColorInputRef.current = el;
                lineColorRegRef(el);
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              control={control}
              name={`lines.${index}.tags` as const}
              render={({ field: tagsField }) => (
                <TagsInput
                  value={tagsField.value ?? []}
                  onChange={tagsField.onChange}
                />
              )}
            />
          </Grid>
        </Grid>

        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
          Coordinates
        </Typography>

        {coordinateFields.map((coordField, coordIndex) => {
          const coordErrors = lineErrors?.coordinates as
            | FieldErrors<CoordinateFormValue>[]
            | undefined;
          const coordError = coordErrors?.[coordIndex];
          const latMessage = getFieldErrorMessage(coordError?.lat);
          const lngMessage = getFieldErrorMessage(coordError?.lng);
          const isDragging = dragSrcIndex === coordIndex;
          const isDropTarget = dragOverIndex === coordIndex && !isDragging;

          return (
            <Grid
              container
              spacing={2}
              key={coordField.id}
              alignItems="center"
              draggable
              onDragStart={() => setDragSrcIndex(coordIndex)}
              onDragOver={(e) => {
                e.preventDefault();
                if (dragSrcIndex !== null && dragSrcIndex !== coordIndex) {
                  setDragOverIndex(coordIndex);
                }
              }}
              onDrop={() => {
                if (dragSrcIndex !== null && dragSrcIndex !== coordIndex) {
                  moveCoordinate(dragSrcIndex, coordIndex);
                }
                setDragSrcIndex(null);
                setDragOverIndex(null);
              }}
              onDragEnd={() => {
                setDragSrcIndex(null);
                setDragOverIndex(null);
              }}
              sx={{
                mb: 1,
                opacity: isDragging ? 0.4 : 1,
                outline: isDropTarget
                  ? (theme) => `2px solid ${theme.palette.primary.main}`
                  : "none",
                borderRadius: 1,
              }}
            >
              <Grid
                item
                xs="auto"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mt: "16px",
                }}
              >
                <DragIndicatorIcon
                  fontSize="small"
                  sx={{ cursor: "grab", color: "text.disabled" }}
                />
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    bgcolor: watchedLineColor || "primary.main",
                    border: "2px solid white",
                    boxShadow: "0 0 4px rgba(0,0,0,0.4)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "9px",
                    fontWeight: 700,
                    color: "white",
                    flexShrink: 0,
                  }}
                >
                  {coordIndex + 1}
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Latitude"
                  InputLabelProps={{ shrink: true }}
                  error={!!latMessage}
                  helperText={latMessage}
                  inputProps={{ step: "any" }}
                  defaultValue={
                    typeof coordField.lat === "number" ? coordField.lat : ""
                  }
                  onFocus={focusThisLine}
                  {...register(
                    `lines.${index}.coordinates.${coordIndex}.lat` as const,
                    {
                      setValueAs: (value) =>
                        value === "" || value === null
                          ? undefined
                          : parseFloat(value),
                    },
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Longitude"
                  InputLabelProps={{ shrink: true }}
                  error={!!lngMessage}
                  helperText={lngMessage}
                  inputProps={{ step: "any" }}
                  defaultValue={
                    typeof coordField.lng === "number" ? coordField.lng : ""
                  }
                  onFocus={focusThisLine}
                  {...register(
                    `lines.${index}.coordinates.${coordIndex}.lng` as const,
                    {
                      setValueAs: (value) =>
                        value === "" || value === null
                          ? undefined
                          : parseFloat(value),
                    },
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3} lg={2}>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <IconButton
                    aria-label="Populate with current location"
                    onClick={() => handleUseCurrentLocation(coordIndex)}
                    disabled={isFetchingLocation}
                  >
                    <MyLocationIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    aria-label="Remove coordinate"
                    disabled={coordinateFields.length <= 1}
                    onClick={() => removeCoordinate(coordIndex)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
            </Grid>
          );
        })}

        <Button
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => appendCoordinate({ lat: undefined, lng: undefined })}
        >
          Add coordinate
        </Button>
      </AccordionDetails>
    </Accordion>
  );
  },
);

export default InteractiveMapEditor;
